import Phaser from 'phaser';
import { BATTLE_W, COLORS, GAME_HEIGHT, GAME_WIDTH, MAP_FIELD_BOTTOM, MAP_FRAME, UI_BOTTOM_H, UI_TOP_H } from '../config/constants';
import { audio } from '../core/AudioSynth';
import { navigateTo } from '../core/SceneTransition';
import { saveManager } from '../core/SaveManager';
import { mapService } from '../core/MapService';
import {
  createUserMap, deleteUserMap, getMapLayout, getMapWithLayout, getUserMap, listUserMaps, saveMapLayout,
  saveUserMap, userMapToMapDef, type MapEnvironment, type MapLayoutEdit, type MapPerspectivePlane, type PathPoint, type UserMapDef,
} from '../data/map';
import { MAP_PROP_FRAMES, MAP_PROP_HEIGHTS, MAP_PROP_KINDS, MAP_PROP_LABELS, MAP_PROP_TEXTURES, makeMapProp, type MapPropDef, type MapPropKind } from '../data/mapProps';
import { MISSIONS, getMission } from '../data/missions';
import { isFieldTurretPlacementSafe } from '../sim/PlacementRules';
import { buildRoadRibbon, getRoadHalfWidth, smoothRoadPoints } from '../sim/RoadGeometry';
import { addAudioToggleButtons, addFullscreenIconButton, addRetroButtonHover, drawRetroPanel, drawScanlines } from '../ui/Retro';
import { applyUiTextScale } from '../ui/UiText';

type ForgeTool = 'select' | 'node' | 'pad' | 'erase-pad' | 'lane' | 'prop' | 'pan';

interface NodeSelection {
  route: number;
  node: number;
}

const clonePointSafe = (point: PathPoint | null | undefined, fallback: PathPoint): PathPoint => (
  point && Number.isFinite(point.x) && Number.isFinite(point.y) ? { x: point.x, y: point.y } : { ...fallback }
);

const cloneLayout = (layout: MapLayoutEdit): MapLayoutEdit => ({
  routes: (layout.routes ?? []).map((route) => (Array.isArray(route) ? route : []).map((point) => clonePointSafe(point, layout.command ?? { x: 0, y: 0 }))),
  pads: layout.pads.map((pad) => ({ ...pad })),
  entry: { ...layout.entry },
  command: { ...layout.command },
  props: layout.props?.map((prop) => ({ ...prop })),
  environment: layout.environment ? { ...layout.environment } : undefined,
  plane: layout.plane ? {
    ...layout.plane,
    corners: layout.plane.corners?.map((point) => ({ ...point })) as MapPerspectivePlane['corners'],
  } : undefined,
  background: layout.background ? { ...layout.background } : undefined,
});

interface NodeDrag {
  route: number;
  node: number;
  target: PathPoint;
  velocity: PathPoint;
  released: boolean;
  settleMs: number;
}

type ContextTarget = { kind: 'node'; route: number; node: number } | { kind: 'pad'; index: number } | { kind: 'spawn'; route: number } | { kind: 'base' } | { kind: 'prop'; id: string } | { kind: 'canvas' };

/**
 * DEV / MAP FORGE is an authoring surface, not a debug overlay. It edits the
 * exact screen-space coordinates consumed by BattleScene and persists them
 * to the shared campaign store when the commander presses SAVE.
 */
export class MapForgeScene extends Phaser.Scene {
  private missionId = 'aurora';
  private openNewMapOnReady = false;
  private userMapId?: string;
  private userMap?: UserMapDef;
  private layout!: MapLayoutEdit;
  private tool: ForgeTool = 'select';
  private orthogonal = false;
  private selected: NodeSelection = { route: 0, node: 0 };
  private history: MapLayoutEdit[] = [];
  private future: MapLayoutEdit[] = [];
  private dirty = false;
  private status = 'SELECT A ROUTE NODE, SPAWN, BASE OR TURRET PAD';
  private layoutGraphics?: Phaser.GameObjects.Graphics;
  private handleLayer?: Phaser.GameObjects.Container;
  private planeGraphics?: Phaser.GameObjects.Graphics;
  private planeHandleLayer?: Phaser.GameObjects.Container;
  private planeMoveHandle?: Phaser.GameObjects.Arc;
  private planeRotateHandle?: Phaser.GameObjects.Arc;
  private planeCornerHandles: Phaser.GameObjects.Arc[] = [];
  private planeRotationDragging = false;
  private nodeHandles = new Map<string, Phaser.GameObjects.Arc>();
  private activeNodeDrag?: NodeDrag;
  private statusText?: Phaser.GameObjects.Text;
  private toolText?: Phaser.GameObjects.Text;
  private missionText?: Phaser.GameObjects.Text;
  private selectedPadIndex = -1;
  private selectedPropId?: string;
  private activePropKind: MapPropKind = 'wrecked-tank';
  private propPaletteOffset = 0;
  private losPreview = false;
  private worldObjects: Phaser.GameObjects.GameObject[] = [];
  private uiCam?: Phaser.Cameras.Scene2D.Camera;
  // Start with the entire imported plate visible so its image edge is also
  // the obvious authoring edge.
  private editorZoom = 0.67;
  private editorScrollX = 0;
  private editorScrollY = UI_TOP_H;
  private panDrag?: { x: number; y: number; scrollX: number; scrollY: number; mode: 'camera' | 'background' | 'overlay' };
  /** When true, PAN left-drag nudges the plate (and optionally the map) instead of the view. */
  private plateNudgeMode = false;
  /** Keep routes/pads locked to the terrain plate while nudging. */
  private anchorMapToPlate = true;
  /** What the PAN tool / plate nudge moves. */
  private panTarget: 'view' | 'plate' | 'overlay' | 'anchor' = 'view';
  /** Alignment tools live in the right inspector dock, not a floating card. */
  private alignmentDocked = true;
  private backgroundPlate?: Phaser.GameObjects.Image;
  private devToolsOpen = false;
  private sidebarSection: 'files' | 'tools' | 'control' | 'layers' = 'files';
  private readonly mapViewport = { x: 132, y: UI_TOP_H + 8, width: 858, height: 510 };
  // Start in true free placement. Grid assist is opt-in from the alignment
  // card or the node context menu.
  private snapStep = 0;
  private libraryOpen = false;
  private alignmentCollapsed = false;
  private alignmentPanel?: Phaser.GameObjects.Container;
  private alignmentPanelX = 938;
  private alignmentPanelY = 118;
  private alignmentPanelPositionLoaded = false;
  private contextMenu?: Phaser.GameObjects.Container;
  private renameOverlay?: HTMLDivElement;
  private renameInput?: HTMLInputElement;
  private rightClickHandled = false;
  private padPulse = 0;
  private readonly onGlobalPointer = (pointer: Phaser.Input.Pointer, over: Phaser.GameObjects.GameObject[]) => {
    if (pointer.button === 2) {
      if (this.rightClickHandled) {
        this.rightClickHandled = false;
        return;
      }
      if (this.contextMenu) {
        this.closeContextMenu();
        return;
      }
      if (!over.length && this.isInMapViewport(pointer.x, pointer.y)) this.openContextMenu(pointer.x, pointer.y, { kind: 'canvas' });
      return;
    }
    if ((pointer.button !== 0 && pointer.button !== 1 && pointer.button !== 2) || !this.isInMapViewport(pointer.x, pointer.y)) return;
    // Middle-drag / Shift+left pans the view (map + plate together).
    // Default PAN left-drag does the same. Plate NUDGE (alignment) slides the plate.
    if (pointer.button === 1 || this.tool === 'pan') {
      if (pointer.button === 2 && this.tool !== 'pan') return;
      const shift = !!(pointer.event as MouseEvent | undefined)?.shiftKey;
      let mode: 'camera' | 'background' | 'overlay' = 'camera';
      if (pointer.button === 0 && !shift && this.tool === 'pan') {
        if (this.panTarget === 'plate' && this.backgroundPlate) {
          mode = 'background';
          this.anchorMapToPlate = false;
        } else if (this.panTarget === 'overlay') {
          mode = 'overlay';
        } else if (this.panTarget === 'anchor' && this.backgroundPlate) {
          mode = 'background';
          this.anchorMapToPlate = true;
        } else {
          mode = 'camera';
        }
      }
      this.plateNudgeMode = mode === 'background';
      this.closeContextMenu();
      this.releaseDragState();
      this.panDrag = {
        x: pointer.x,
        y: pointer.y,
        scrollX: this.cameras.main?.scrollX ?? 0,
        scrollY: this.cameras.main?.scrollY ?? 0,
        mode,
      };
      this.setCanvasCursor('grabbing');
      return;
    }
    if (over.length) return;
    this.closeContextMenu();
    const world = this.cameras.main?.getWorldPoint(pointer.x, pointer.y) ?? { x: pointer.x, y: pointer.y };
    this.onCanvasPress(world.x, world.y);
  };
  private readonly onGameObjectPointer = (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.GameObject) => {
    if (pointer.button !== 2 || this.contextMenu) return;
    const target = gameObject.getData('forgeContextTarget') as ContextTarget | undefined;
    if (target) {
      this.rightClickHandled = true;
      this.openContextMenu(pointer.x, pointer.y, target);
      globalThis.setTimeout(() => { this.rightClickHandled = false; }, 0);
    }
  };
  private readonly onEsc = () => navigateTo(this, 'Command', { tab: 'mission' });
  private readonly onSaveKey = () => { void this.saveLayout(); };
  private readonly onUndoKey = () => this.undo();
  private readonly onRedoKey = () => this.redo();
  private readonly onPointerMove = (pointer: Phaser.Input.Pointer) => {
    if (this.tool === 'pan' || this.panDrag) {
      this.setCanvasCursor(pointer.isDown && this.panDrag ? 'grabbing' : this.isInMapViewport(pointer.x, pointer.y) ? 'grab' : 'default');
    }
    if (!this.panDrag || !pointer.isDown) return;
    const cam = this.cameras.main;
    if (!cam) { this.panDrag = undefined; return; }
    const dx = pointer.x - this.panDrag.x;
    const dy = pointer.y - this.panDrag.y;
    if (this.panDrag.mode === 'overlay') {
      const ox = -dx / cam.zoom;
      const oy = -dy / cam.zoom;
      const plane = this.layout.plane ?? (this.layout.plane = {
        x: GAME_WIDTH / 2, y: (UI_TOP_H + MAP_FIELD_BOTTOM) / 2,
        rotation: 0, tilt: 0.766, scale: 1, visible: true, farScale: 0.68, nearScale: 1.1,
      });
      plane.x += ox;
      plane.y += oy;
      plane.corners?.forEach((corner) => { corner.x += ox; corner.y += oy; });
      this.panDrag = { ...this.panDrag, x: pointer.x, y: pointer.y };
      this.drawPerspectivePlane(this.time?.now ?? 0);
      this.syncPlaneHandlePositions();
      this.dirty = true;
      this.statusText?.setText(`ISO OVERLAY  ${Math.round(plane.x)},${Math.round(plane.y)}  ·  BACKGROUND FIXED`);
      return;
    }
    if (this.panDrag.mode === 'background') {
      const bg = this.layout.background ?? (this.layout.background = { offsetX: 0, offsetY: 0, scale: 1 });
      const ox = -dx / cam.zoom;
      const oy = -dy / cam.zoom;
      bg.offsetX = Phaser.Math.Clamp(Number(((bg.offsetX ?? 0) + ox).toFixed(1)), -900, 900);
      bg.offsetY = Phaser.Math.Clamp(Number(((bg.offsetY ?? 0) + oy).toFixed(1)), -500, 500);
      bg.scale ??= 1;
      // Keep authored geometry locked to the plate when ANCHOR MAP is on.
      if (this.anchorMapToPlate) this.translateLayout(ox, oy);
      this.panDrag = { ...this.panDrag, x: pointer.x, y: pointer.y };
      this.redrawBackgroundPlate();
      if (this.anchorMapToPlate) this.syncWorldHandlePositions();
      this.dirty = true;
      this.statusText?.setText(
        this.anchorMapToPlate
          ? `MAP ANCHORED TO PLATE  ${Math.round(bg.offsetX ?? 0)},${Math.round(bg.offsetY ?? 0)}`
          : `BACKGROUND PLATE ONLY  ${Math.round(bg.offsetX ?? 0)},${Math.round(bg.offsetY ?? 0)}  ·  HUD/ROUTES FIXED`,
      );
      this.toolText?.setText(`BG ${Math.round(bg.offsetX ?? 0)},${Math.round(bg.offsetY ?? 0)} · ${this.dirty ? 'UNSAVED' : 'SAVED'}`);
      return;
    }
    const bounds = this.editorPanBounds();
    cam.setScroll(
      Phaser.Math.Clamp(this.panDrag.scrollX - dx / cam.zoom, bounds.minX, bounds.maxX),
      Phaser.Math.Clamp(this.panDrag.scrollY - dy / cam.zoom, bounds.minY, bounds.maxY),
    );
    this.editorScrollX = cam.scrollX;
    this.editorScrollY = cam.scrollY;
  };
  private readonly onPointerUp = () => {
    const wasBackground = this.panDrag?.mode === 'background';
    this.panDrag = undefined;
    if (this.tool === 'pan') {
      this.setCanvasCursor('grab');
      if (wasBackground) {
        this.status = this.anchorMapToPlate
          ? 'MAP + PLATE MOVED TOGETHER — SAVE WHEN ALIGNMENT IS RIGHT'
          : 'BACKGROUND MOVED — ROUTES STAYED FIXED · SAVE LAYOUT';
        this.markDirty(this.status);
      }
    } else {
      this.setCanvasCursor('default');
    }
  };
  private readonly onWheel = (pointer: Phaser.Input.Pointer, _gameObjects: Phaser.GameObjects.GameObject[], _dx: number, dy: number) => {
    if (!this.isInMapViewport(pointer.x, pointer.y)) return;
    const cam = this.cameras.main;
    if (!cam) return;
    const before = cam.getWorldPoint(pointer.x, pointer.y);
    this.editorZoom = Phaser.Math.Clamp(this.editorZoom - dy * 0.001, 0.48, 1.6);
    cam.setZoom(this.editorZoom);
    const after = cam.getWorldPoint(pointer.x, pointer.y);
    cam.scrollX += before.x - after.x;
    cam.scrollY += before.y - after.y;
    const bounds = this.editorPanBounds();
    this.editorScrollX = Phaser.Math.Clamp(cam.scrollX, bounds.minX, bounds.maxX);
    this.editorScrollY = Phaser.Math.Clamp(cam.scrollY, bounds.minY, bounds.maxY);
    cam.setScroll(this.editorScrollX, this.editorScrollY);
    this.updateEditorStatus();
    if (this.tool === 'pan') this.setCanvasCursor('grab');
  };

  constructor() {
    super('MapForge');
  }

  preload(): void {
    this.load.image(MAP_PROP_TEXTURES['wrecked-tank'], 'assets/art/props/wrecked-tank-v1.png');
    this.load.image(MAP_PROP_TEXTURES['aircraft-wreck'], 'assets/art/props/aircraft-wreck-v1.png');
    this.load.image(MAP_PROP_TEXTURES.barrels, 'assets/art/props/barrels-v1.png');
    this.load.image(MAP_PROP_TEXTURES.crates, 'assets/art/props/crates-v1.png');
    this.load.image(MAP_PROP_TEXTURES.debris, 'assets/art/props/debris-v1.png');
    this.load.image(MAP_PROP_TEXTURES['turret-wreck'], 'assets/art/top-ballistic-v3.png');
    this.load.image(MAP_PROP_TEXTURES['weapon-cache'], 'assets/art/tower-artillery-v4.png');
    this.load.image(MAP_PROP_TEXTURES['sensor-array'], 'assets/art/ability-drone.png');
    this.load.image(MAP_PROP_TEXTURES['symbiosis-pod'], 'assets/art/symbiosis-pod-arc-v1.png');
    this.load.image(MAP_PROP_TEXTURES['command-crate'], 'assets/art/command-emblem.png');
    this.load.spritesheet('map-prop-environmental-life', 'assets/art/world/map-environmental-life-v1.png', { frameWidth: 512, frameHeight: 512 });
  }

  init(data?: { missionId?: string; userMapId?: string; openLibrary?: boolean; openNewMap?: boolean }): void {
    this.libraryOpen = data?.openLibrary === true;
    this.devToolsOpen = false;
    this.openNewMapOnReady = data?.openNewMap === true;
    this.userMapId = data?.userMapId;
    this.userMap = data?.userMapId ? getUserMap(data.userMapId) : undefined;
    if (data?.missionId && MISSIONS.some((mission) => mission.id === data.missionId)) this.missionId = data.missionId;
  }

  create(): void {
    this.loadAlignmentPanelPosition();
    const missingUserMapId = this.userMapId && !this.userMap ? this.userMapId : undefined;
    if (this.userMap) this.loadUserMap(this.userMap);
    else this.loadMission(this.missionId);
    if (missingUserMapId) {
      this.status = `USER MAP ${missingUserMapId} NOT FOUND — LOADED CAMPAIGN LAYOUT INSTEAD`;
      this.statusText?.setText(this.status);
    }
    this.input.mouse?.disableContextMenu();
    this.input.on('pointerdown', this.onGlobalPointer);
    this.input.on('gameobjectdown', this.onGameObjectPointer);
    this.input.on('pointermove', this.onPointerMove);
    this.input.on('pointerup', this.onPointerUp);
    this.input.on('wheel', this.onWheel);
    this.input.keyboard?.on('keydown-ESC', this.onEsc);
    this.input.keyboard?.on('keydown-S', this.onSaveKey);
    this.input.keyboard?.on('keydown-Z', this.onUndoKey);
    this.input.keyboard?.on('keydown-Y', this.onRedoKey);
    this.applyToolCursor();
    // Losing the pointer mid-drag must not leave pan stuck on.
    this.input.on('pointerupoutside', this.onPointerUp);
    window.addEventListener('blur', this.onPointerUp);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.off('pointerdown', this.onGlobalPointer);
      this.input.off('gameobjectdown', this.onGameObjectPointer);
      this.input.off('pointermove', this.onPointerMove);
      this.input.off('pointerup', this.onPointerUp);
      this.input.off('pointerupoutside', this.onPointerUp);
      this.input.off('wheel', this.onWheel);
      window.removeEventListener('blur', this.onPointerUp);
      this.closeContextMenu();
      this.closeSaveAsModal();
      this.setCanvasCursor('default');
      this.input.keyboard?.off('keydown-ESC', this.onEsc);
      this.input.keyboard?.off('keydown-S', this.onSaveKey);
      this.input.keyboard?.off('keydown-Z', this.onUndoKey);
      this.input.keyboard?.off('keydown-Y', this.onRedoKey);
    });
  }

  private setCanvasCursor(cursor: string): void {
    const canvas = this.game?.canvas;
    if (typeof document !== 'undefined') {
      const body = document.body;
      body.classList.toggle('apogee-forge-pan', cursor === 'grab');
      body.classList.toggle('apogee-forge-pan-grab', cursor === 'grabbing');
    }
    if (!canvas) return;
    canvas.style.cursor = cursor;
    try { this.input.setDefaultCursor(cursor); } catch { /* older Phaser builds */ }
  }

  private applyToolCursor(): void {
    this.setCanvasCursor(this.tool === 'pan' ? 'grab' : 'default');
  }

  /** World-space scroll limits for the editor camera viewport. */
  private editorPanBounds(): { minX: number; maxX: number; minY: number; maxY: number } {
    const cam = this.cameras.main;
    const zoom = cam?.zoom || this.editorZoom || 1;
    const viewW = (cam?.width || this.mapViewport.width) / zoom;
    const viewH = (cam?.height || this.mapViewport.height) / zoom;
    const bg = this.layout?.background;
    const platePadX = Math.abs(bg?.offsetX ?? 0) + 40;
    const platePadY = Math.abs(bg?.offsetY ?? 0) + 40;
    // Extra slack so pan remains possible even when the zoomed-out view is
    // larger than the authored world (otherwise camera pan is a no-op).
    const margin = 160;
    const worldW = Math.max(GAME_WIDTH + platePadX, viewW - margin) + margin * 2;
    const worldBottom = Math.max(MAP_FIELD_BOTTOM + platePadY, UI_TOP_H + viewH - margin) + margin;
    return {
      minX: -margin,
      maxX: Math.max(margin, worldW - viewW + margin),
      minY: UI_TOP_H - margin,
      maxY: Math.max(UI_TOP_H + margin, worldBottom - viewH + margin),
    };
  }

  /** Shift every authored world point by the same delta (plate-anchored pan). */
  private translateLayout(dx: number, dy: number): void {
    if (!dx && !dy) return;
    const move = (point?: PathPoint | null) => {
      if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
      point.x += dx;
      point.y += dy;
    };
    this.layout.routes.forEach((route) => route.forEach(move));
    this.layout.pads.forEach(move);
    move(this.layout.entry);
    move(this.layout.command);
    (this.layout.props ?? []).forEach((prop) => { prop.x += dx; prop.y += dy; });
    const plane = this.layout.plane;
    if (plane) {
      plane.x += dx;
      plane.y += dy;
      plane.corners?.forEach(move);
    }
  }

  /** Live-update world handles after an anchored plate move (no full rebuild). */
  private syncWorldHandlePositions(): void {
    this.layout.routes.forEach((route, routeIndex) => {
      route.forEach((point, nodeIndex) => {
        this.nodeHandles.get(`${routeIndex}:${nodeIndex}`)?.setPosition(point.x, point.y);
      });
    });
    // Pad / spawn / base handles live in handleLayer — rebuild that layer only.
    if (this.handleLayer) {
      this.handleLayer.removeAll(true);
      this.nodeHandles.clear();
      this.drawProps();
      this.layout.routes.forEach((route, routeIndex) => {
        route.forEach((point, nodeIndex) => this.addNodeHandle(point, routeIndex, nodeIndex));
      });
      this.layout.pads.forEach((pad, index) => this.addPadHandle(pad, index));
      this.addSpawnHandles();
      this.addBaseHandle();
      if (this.tool === 'pan') this.disableWorldInteractionForPan();
    }
  }

  private redrawBackgroundPlate(): void {
    const plate = this.backgroundPlate;
    if (!plate) return;
    const bg = this.layout.background ?? { offsetX: 0, offsetY: 0, scale: 1 };
    const baseCrop = Math.max(MAP_FRAME.width / Math.max(1, plate.width), MAP_FRAME.height / Math.max(1, plate.height)) * 1.08;
    plate
      .setPosition(
        MAP_FRAME.x + MAP_FRAME.width / 2 + (bg.offsetX ?? 0),
        MAP_FRAME.y + MAP_FRAME.height / 2 + (bg.offsetY ?? 0),
      )
      .setScale(baseCrop * (bg.scale ?? 1));
  }

  update(time: number, delta: number): void {
    this.stepNodeInertia(delta);
    this.padPulse = time;
    this.drawPerspectivePlane(time);
  }

  private loadMission(missionId: string): void {
    this.missionId = missionId;
    this.userMap = undefined;
    this.userMapId = undefined;
    const mission = getMission(missionId);
    this.layout = cloneLayout(getMapLayout(mission.mapId));
    this.ensurePlane();
    this.history = [];
    this.future = [];
    this.selected = { route: 0, node: 0 };
    this.tool = 'select';
    this.dirty = false;
    this.status = `EDITING ${mission.name}  ·  ROUTES, PADS, SPAWN AND BASTION ARE LIVE`;
    this.editorZoom = 0.67;
    this.editorScrollX = 0;
    this.editorScrollY = UI_TOP_H;
    this.render();
    this.applyToolCursor();
    this.openNewMapIfRequested();
  }

  private loadUserMap(userMap: UserMapDef): void {
    this.userMap = userMap;
    this.userMapId = userMap.id;
    this.layout = cloneLayout(userMap.layout);
    this.ensurePlane();
    this.history = [];
    this.future = [];
    this.selected = { route: 0, node: 0 };
    this.tool = 'select';
    this.dirty = false;
    // Custom plates need a slightly tighter default view so pan has room to move.
    this.editorZoom = userMap.backgroundDataUrl ? 0.9 : 0.67;
    this.editorScrollX = 0;
    this.editorScrollY = UI_TOP_H;
    this.status = `EDITING ${userMap.name}  ·  USER MAP · DRAG HANDLES, SAVE UPDATES THIS MAP`;
    this.render();
    this.applyToolCursor();
    this.openNewMapIfRequested();
  }

  private openNewMapIfRequested(): void {
    if (!this.openNewMapOnReady) return;
    this.openNewMapOnReady = false;
    this.time.delayedCall(0, () => this.openNewMapModal());
  }

  private render(): void {
    while (this.cameras.cameras.length > 1) this.cameras.remove(this.cameras.cameras[1]);
    this.cameras.main.setViewport(0, 0, GAME_WIDTH, GAME_HEIGHT);
    this.cameras.main.setZoom(1);
    this.cameras.main.setScroll(0, 0);
    this.uiCam = undefined;
    this.children.removeAll(true);
    // removeAll just destroyed the menu, so the reference is now dangling.
    this.contextMenu = undefined;
    const g = this.add.graphics();
    g.fillStyle(0x05080c, 1).fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    drawScanlines(g, GAME_WIDTH, GAME_HEIGHT, 0.05, 4);

    const mission = getMission(this.missionId);
    const map = this.userMap ? userMapToMapDef(this.userMap) : getMapWithLayout(mission.mapId);
    const bgKey = this.userMap?.backgroundDataUrl ? `user-map-${this.userMap.id}-bg` : `battle-${this.missionId}-bg`;
    if (this.userMap?.backgroundDataUrl && !this.textures.exists(bgKey)) {
      this.load.image(bgKey, this.userMap.backgroundDataUrl);
      this.load.once(Phaser.Loader.Events.COMPLETE, () => this.render());
      this.load.start();
    }
    this.backgroundPlate = undefined;
    if (this.textures.exists(bgKey)) {
      const bg = this.layout.background ?? { offsetX: 0, offsetY: 0, scale: 1 };
      const plate = this.add.image(
        MAP_FRAME.x + MAP_FRAME.width / 2 + (bg.offsetX ?? 0),
        MAP_FRAME.y + MAP_FRAME.height / 2 + (bg.offsetY ?? 0),
        bgKey,
      );
      const crop = Math.max(MAP_FRAME.width / Math.max(1, plate.width), MAP_FRAME.height / Math.max(1, plate.height)) * 1.08;
      plate.setScale(crop * (bg.scale ?? 1)).setAlpha(0.96).setDepth(0);
      this.backgroundPlate = plate;
    }
    // Keep the authored plate visible. The old 0.28 wash plus the opaque
    // canvas panel made the editor look almost black and hid terrain detail.
    g.fillStyle(0x061016, 0.07).fillRect(MAP_FRAME.x, MAP_FRAME.y, MAP_FRAME.width, MAP_FRAME.height);
    // The authoring grid is intentionally finer than the tactical 32px guide.
    // It gives visual feedback for the 1/2/4px assistive snap without making
    // the terrain look like a coarse checkerboard.
    if (this.snapStep > 0) {
      g.lineStyle(1, 0x6ed8ff, 0.055);
      for (let x = MAP_FRAME.x; x <= MAP_FRAME.x + MAP_FRAME.width; x += 16) g.lineBetween(x, MAP_FRAME.y, x, MAP_FIELD_BOTTOM);
      for (let y = MAP_FRAME.y; y <= MAP_FIELD_BOTTOM; y += 16) g.lineBetween(MAP_FRAME.x, y, MAP_FRAME.x + MAP_FRAME.width, y);
    }

    this.planeGraphics = this.add.graphics().setDepth(2);
    this.planeHandleLayer = this.add.container(0, 0).setDepth(8);
    this.drawPerspectivePlane(0);
    this.addPerspectivePlaneHandles();
    this.layoutGraphics = this.add.graphics().setDepth(3);
    this.handleLayer = this.add.container(0, 0).setDepth(6);
    this.releaseDragState();
    this.drawLayout();
    this.disableWorldInteractionForPan();
    this.worldObjects = [...this.children.list];
    this.drawHud(g, this.userMap?.name ?? mission.name, map.briefing);
    this.setupEditorCameras();
  }

  private isInMapViewport(x: number, y: number): boolean {
    return x >= this.mapViewport.x && x <= this.mapViewport.x + this.mapViewport.width
      && y >= this.mapViewport.y && y <= this.mapViewport.y + this.mapViewport.height;
  }

  private disableWorldInteractionForPan(): void {
    if (this.tool !== 'pan') return;
    this.releaseDragState();
    for (const layer of [this.handleLayer, this.planeHandleLayer]) {
      layer?.list.forEach((child) => {
        const object = child as Phaser.GameObjects.GameObject & { disableInteractive?: () => void; removeInteractive?: () => void };
        object.removeInteractive?.();
        object.disableInteractive?.();
      });
    }
    // Also neutralise any leftover world hit-targets so pointerdown always
    // reaches the scene pan handler.
    for (const child of this.children.list) {
      const object = child as Phaser.GameObjects.GameObject & {
        disableInteractive?: () => void;
        input?: { enabled?: boolean };
        depth?: number;
      };
      if ((object.depth ?? 0) >= 100) continue; // HUD stays clickable
      if (object === this.handleLayer || object === this.planeHandleLayer) continue;
      object.disableInteractive?.();
      if (object.input) object.input.enabled = false;
    }
  }

  /** Cancel in-flight Phaser drag streams before their targets are destroyed. */
  private releaseDragState(opts: { keepPan?: boolean } = {}): void {
    try {
      const manager = this.input.manager;
      for (const pointer of manager.pointers) {
        const p = pointer as Phaser.Input.Pointer & { dragging?: boolean };
        if (p.dragging) p.dragging = false;
      }
      const plugin = this.input as unknown as { dragState?: number[]; _drag?: unknown[] };
      if (Array.isArray(plugin.dragState)) plugin.dragState.fill(0);
      if (Array.isArray(plugin._drag)) plugin._drag.length = 0;
    } catch { /* best effort — never block a redraw on input cleanup */ }
    this.activeNodeDrag = undefined;
    if (!opts.keepPan) this.panDrag = undefined;
  }

  private setupEditorCameras(): void {
    const cam = this.cameras.main;
    cam.setViewport(this.mapViewport.x, this.mapViewport.y, this.mapViewport.width, this.mapViewport.height);
    cam.setZoom(this.editorZoom);
    const bounds = this.editorPanBounds();
    this.editorScrollX = Phaser.Math.Clamp(this.editorScrollX, bounds.minX, bounds.maxX);
    this.editorScrollY = Phaser.Math.Clamp(this.editorScrollY, bounds.minY, bounds.maxY);
    cam.setScroll(this.editorScrollX, this.editorScrollY);
    const hud = this.children.list.filter((object) => !this.worldObjects.includes(object));
    if (this.uiCam) {
      this.cameras.remove(this.uiCam, true);
      this.uiCam = undefined;
    }
    this.uiCam = this.cameras.add(0, 0, GAME_WIDTH, GAME_HEIGHT);
    this.uiCam.setZoom(1);
    this.uiCam.centerOn(GAME_WIDTH / 2, GAME_HEIGHT / 2);
    this.uiCam.ignore(this.worldObjects);
    cam.ignore(hud);
  }

  private drawHud(_g: Phaser.GameObjects.Graphics, name: string, briefing: string): void {
    const ui = this.add.graphics().setDepth(100);
    ui.fillStyle(0x071017, 0.98).fillRect(0, 0, GAME_WIDTH, 56);
    ui.lineStyle(1.5, 0x315162, 1).lineBetween(0, 56, GAME_WIDTH, 56);
    drawRetroPanel(ui, 8, 6, GAME_WIDTH - 16, 44, 0x294f61, 1, 10);
    this.add.text(24, 13, 'APOGEE HOLD', { fontFamily: 'Consolas, monospace', fontSize: '17px', color: '#e3f5ff', fontStyle: 'bold', letterSpacing: 1 }).setDepth(101);
    this.add.text(215, 15, 'MAP FORGE', { fontFamily: 'Consolas, monospace', fontSize: '13px', color: '#e0a744', fontStyle: 'bold', letterSpacing: 1 }).setDepth(101);
    this.missionText = this.add.text(350, 13, name.toUpperCase(), { fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#d4a24a' }).setDepth(101);
    this.add.text(350, 31, briefing.slice(0, 72), { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#6f8e9e' }).setDepth(101);
    this.add.text(920, 14, 'DRAFT', { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#6f9eaf' }).setDepth(101);
    this.add.text(920, 28, this.dirty ? 'UNSAVED' : 'SAVED', { fontFamily: 'Consolas, monospace', fontSize: '10px', color: this.dirty ? '#e0a744' : '#7dce5a' }).setDepth(101);
    this.dockButton(710, 28, 70, 'MISSIONS', () => navigateTo(this, 'Command', { tab: 'mission' }), false, 0x152028, 9);
    this.dockButton(790, 28, 70, 'PLAY MAP', () => { void this.playTestMap(); }, false, 0x1c3324, 9);
    if (saveManager.data.settings.devMode) this.dockButton(870, 28, 68, 'DEV TOOLS', () => { this.devToolsOpen = !this.devToolsOpen; this.render(); }, this.devToolsOpen, this.devToolsOpen ? 0x3d3020 : 0x152028, 8);
    addAudioToggleButtons(this, 972, 28).setDepth(101);
    this.dockButton(1072, 28, 62, 'UNDO', () => this.undo(), this.history.length > 0);
    this.dockButton(1150, 28, 62, 'SAVE', () => { if (this.dirty) void this.saveLayout(); else { this.status = 'NO UNSAVED CHANGES'; this.render(); } }, this.dirty, this.dirty ? 0x1c3b30 : 0x152028, 9);
    addFullscreenIconButton(this, 1238, 28).setDepth(102);

    this.drawExplorerSidebar(ui);

    // The map is a document canvas, not a dark modal card: draw a frame and
    // corner brackets without the opaque RetroPanel fill.
    ui.fillStyle(0x071017, 0.08).fillRect(this.mapViewport.x, this.mapViewport.y, this.mapViewport.width, this.mapViewport.height);
    ui.lineStyle(1.5, 0x58cbe8, 0.9).strokeRect(this.mapViewport.x, this.mapViewport.y, this.mapViewport.width, this.mapViewport.height);
    ui.lineStyle(2, 0x8beaff, 0.78);
    ui.lineBetween(this.mapViewport.x, this.mapViewport.y, this.mapViewport.x + 22, this.mapViewport.y);
    ui.lineBetween(this.mapViewport.x, this.mapViewport.y, this.mapViewport.x, this.mapViewport.y + 22);
    ui.lineBetween(this.mapViewport.x + this.mapViewport.width, this.mapViewport.y, this.mapViewport.x + this.mapViewport.width - 22, this.mapViewport.y);
    ui.lineBetween(this.mapViewport.x + this.mapViewport.width, this.mapViewport.y, this.mapViewport.x + this.mapViewport.width, this.mapViewport.y + 22);
    this.add.text(this.mapViewport.x + 14, this.mapViewport.y + 12, 'TACTICAL CANVAS', { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#9eddf5', fontStyle: 'bold' }).setDepth(101);
    this.add.text(this.mapViewport.x + this.mapViewport.width - 14, this.mapViewport.y + 12, this.tool === 'pan'
      ? `PAN: ${this.panTarget.toUpperCase()}`
      : 'WHEEL ZOOM  ·  PAN TOOL / MIDDLE DRAG', { fontFamily: 'Consolas, monospace', fontSize: '7px', color: '#6f9eaf' }).setOrigin(1, 0).setDepth(101);

    drawRetroPanel(ui, 1002, 66, 266, 646, 0x274959, 0.96, 10);
    this.drawEditorInspector();

    drawRetroPanel(ui, this.mapViewport.x, 584, this.mapViewport.width, 128, 0x274959, 0.98, 10);
    this.add.text(this.mapViewport.x + 14, 596, 'PROPS  ·  CLICK TO ARM PLACEMENT', { fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#e0a744', fontStyle: 'bold' }).setDepth(101);
    this.drawPropPalette();
    const points = this.layout.routes.reduce((total, route) => total + route.length, 0);
    this.statusText = this.add.text(24, 704, this.status, { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#9eddf5' }).setDepth(101);
    this.toolText = this.add.text(GAME_WIDTH - 20, 704, `LANES ${this.layout.routes.length} · NODES ${points} · PADS ${this.layout.pads.length}/12 · PROPS ${(this.layout.props ?? []).length} · ${this.dirty ? 'UNSAVED' : 'SAVED'}`, { fontFamily: 'Consolas, monospace', fontSize: '8px', color: this.dirty ? '#e0a744' : '#7dce5a' }).setOrigin(1, 0).setDepth(101);
    // Alignment plane tools + the in-forge user-map library must actually draw.
    // They were previously orphaned after a HUD refactor, which made "OPEN MAPS"
    // a dead toggle and left plane fitting unreachable from the sidebar.
    this.drawAlignmentControls(ui);
    if (this.libraryOpen) this.drawUserMapLibrary(ui);
    applyUiTextScale(this);
  }

  private drawExplorerSidebar(ui: Phaser.GameObjects.Graphics): void {
    drawRetroPanel(ui, 12, 66, 106, 506, 0x274959, 0.96, 10);
    this.add.text(24, 78, 'EXPLORER', { fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#e0a744', fontStyle: 'bold', letterSpacing: 1 }).setDepth(101);
    const tabs: Array<[string, 'files' | 'tools' | 'control' | 'layers']> = [['FILES', 'files'], ['TOOLS', 'tools'], ['CONTROL', 'control'], ['LAYERS', 'layers']];
    tabs.forEach(([label, section], index) => {
      const x = 24 + (index % 2) * 46;
      const y = 100 + Math.floor(index / 2) * 25;
      const active = this.sidebarSection === section;
      const button = this.add.rectangle(x + 20, y, 42, 19, active ? 0x1d4a5c : 0x152028, 1).setStrokeStyle(1, active ? 0x64d7ff : 0x335563).setInteractive({ useHandCursor: true }).setDepth(101);
      addRetroButtonHover(button, active ? 0x1d4a5c : 0x152028);
      this.add.text(x + 20, y, label, { fontFamily: 'Consolas, monospace', fontSize: '8px', color: active ? '#e5f8ff' : '#9ac5d7', fontStyle: active ? 'bold' : 'normal' }).setOrigin(0.5).setDepth(102);
      button.on('pointerdown', () => { audio.click(); this.sidebarSection = section; this.render(); });
    });
    if (this.sidebarSection === 'files') {
      this.sidebarButton(64, 178, 'NEW MAP', () => this.createBlankUserMap(), true);
      this.sidebarButton(64, 216, this.libraryOpen ? 'CLOSE MAPS' : 'OPEN MAPS', () => { this.libraryOpen = !this.libraryOpen; this.render(); }, this.libraryOpen);
      this.sidebarButton(64, 254, 'IMPORT IMAGE', () => this.importUserMap(), false, 0x1c3324);
      this.sidebarButton(64, 292, 'SAVE AS', () => this.openSaveAsModal());
      this.sidebarButton(64, 330, 'SAVE', () => { if (this.dirty) void this.saveLayout(); else { this.status = 'NO UNSAVED CHANGES'; this.render(); } }, this.dirty, this.dirty ? 0x1c3b30 : 0x152028);
      this.sidebarButton(64, 368, 'MISSIONS', () => navigateTo(this, 'Command', { tab: 'mission' }), false, 0x152028, 9);
      this.sidebarButton(64, 406, 'PLAY MAP', () => { void this.playTestMap(); }, false, 0x1c3324, 9);
      this.add.text(22, 450, this.userMap ? `FILE\n${this.userMap.name.slice(0, 13)}` : 'CAMPAIGN\nLAYOUT', { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#6f9eaf', lineSpacing: 5 }).setDepth(101);
    } else if (this.sidebarSection === 'tools') {
      const tools: Array<[string, ForgeTool]> = [['SELECT', 'select'], ['PATH', 'node'], ['SPAWN', 'lane'], ['MOUNT', 'pad'], ['ERASE', 'erase-pad'], ['DECOR', 'prop'], ['PAN', 'pan']];
      tools.forEach(([label, tool], index) => this.sidebarButton(64, 174 + index * 39, label, () => this.selectForgeTool(tool), this.tool === tool));
      this.sidebarButton(64, 468, '− NODE', () => this.removeSelectedNode());
    } else if (this.sidebarSection === 'control') {
      this.sidebarButton(64, 178, this.losPreview ? 'LOS ON' : 'LOS VIEW', () => { this.losPreview = !this.losPreview; this.status = `LINE OF SIGHT PREVIEW ${this.losPreview ? 'ENABLED' : 'HIDDEN'}`; this.render(); }, this.losPreview, this.losPreview ? 0x3d3020 : 0x152028);
      this.sidebarButton(64, 218, 'FIT VIEW', () => this.fitEditorView());
      this.sidebarButton(64, 258, 'PLAY TEST', () => { void this.playTestMap(); }, false, 0x1c3324);
      if (saveManager.data.settings.devMode) this.sidebarButton(64, 298, 'DEV TOOLS', () => { this.devToolsOpen = !this.devToolsOpen; this.render(); }, this.devToolsOpen, this.devToolsOpen ? 0x3d3020 : 0x152028);
      this.sidebarButton(64, 350, `TERRAIN · ${(this.layout.environment?.terrain ?? 'rock').toUpperCase()}`, () => this.cycleTerrain(), false, 0x152028, 7);
      this.sidebarButton(64, 388, `WEATHER · ${(this.layout.environment?.weather ?? 'clear').toUpperCase()}`, () => this.cycleWeather(), false, 0x152028, 7);
      this.sidebarButton(64, 426, `INTENSITY · ${Math.round((this.layout.environment?.intensity ?? 0.35) * 100)}%`, () => this.adjustWeatherIntensity(0.1), false, 0x152028, 7);
      this.add.text(22, 468, 'ENVIRONMENT\nTERRAIN + WEATHER\nARE SAVED WITH MAP', { fontFamily: 'Consolas, monospace', fontSize: '7px', color: '#6f9eaf', lineSpacing: 5 }).setDepth(101);
    } else {
      this.sidebarButton(64, 178, `PLANE ${this.plane.visible ? 'ON' : 'OFF'}`, () => { this.plane.visible = !this.plane.visible; this.render(); }, this.plane.visible);
      this.sidebarButton(64, 218, 'ROUTES LIVE', () => { this.status = 'ROUTES ARE LIVE — DRAG AMBER ENDPOINTS OR CYAN CONTROL NODES'; this.render(); });
      this.sidebarButton(64, 258, 'PADS LIVE', () => { this.status = 'PADS ARE LIVE — DRAG FOUNDATIONS OR USE MOUNT MODE'; this.render(); });
      this.sidebarButton(64, 298, 'PROPS LIVE', () => { this.status = 'PROPS ARE LIVE — DRAG, SCALE, ROTATE AND FLAG LOS OCCLUSION'; this.render(); });
      this.add.text(22, 350, 'LAYER GUIDE\nAMBER = ENDPOINT\nCYAN = CONTROL\nORANGE = LOS BLOCK', { fontFamily: 'Consolas, monospace', fontSize: '7px', color: '#6f9eaf', lineSpacing: 5 }).setDepth(101);
    }
  }

  private sidebarButton(yX: number, y: number, label: string, action: () => void, active = false, colour = 0x152028, fontSize = 9): void {
    this.dockButton(yX, y, 82, label, action, active, colour, fontSize);
  }

  private fitEditorView(): void {
    this.editorZoom = Number((this.mapViewport.width / GAME_WIDTH).toFixed(2));
    this.editorScrollX = 0;
    this.editorScrollY = UI_TOP_H;
    this.status = 'VIEW FIT TO MAP — WHEEL ZOOM OR PAN TO INSPECT DETAIL';
    this.render();
  }

  private async playTestMap(): Promise<void> {
    if (this.dirty) await this.saveLayout(false);
    const map = this.userMap ? userMapToMapDef(this.userMap) : getMapWithLayout(getMission(this.missionId).mapId);
    navigateTo(this, 'Battle', {
      missionId: this.missionId,
      customMap: map,
      customBackgroundDataUrl: this.userMap?.backgroundDataUrl,
    });
  }

  private dockButton(x: number, y: number, width: number, label: string, action: () => void, active = false, colour = 0x152028, fontSize = 8): void {
    const bg = this.add.rectangle(x, y, width, 28, active ? 0x1d4a5c : colour, active ? 1 : 0.95)
      .setStrokeStyle(1, active ? 0x64d7ff : 0x335563).setInteractive({ useHandCursor: true }).setDepth(101);
    addRetroButtonHover(bg, active ? 0x1d4a5c : colour);
    bg.on('pointerdown', () => { audio.click(); action(); });
    this.add.text(x, y, label, { fontFamily: 'Consolas, monospace', fontSize: `${fontSize}px`, color: active ? '#e5f8ff' : '#9ac5d7', fontStyle: active ? 'bold' : 'normal' }).setOrigin(0.5).setDepth(101);
  }

  private selectForgeTool(tool: ForgeTool): void {
    this.tool = tool;
    this.status = tool === 'node' ? 'PATH MODE — CLICK TERRAIN TO INSERT A CONTROL POINT'
      : tool === 'pad' ? 'MOUNT MODE — CLICK CLEAR TERRAIN TO ADD A LEGAL TURRET PAD'
        : tool === 'erase-pad' ? 'ERASE MODE — CLICK A PAD TO REMOVE IT'
          : tool === 'prop' ? `${MAP_PROP_LABELS[this.activePropKind]} ARMED — CLICK THE CANVAS TO PLACE IT`
            : tool === 'pan'
              ? (this.panTarget === 'plate'
                ? 'PAN · BACKGROUND ONLY — HUD/ROUTES STAY PUT'
                : this.panTarget === 'overlay'
                  ? 'PAN · ISO OVERLAY ONLY — PLANE MOVES, ART STAYS'
                  : this.panTarget === 'anchor'
                    ? 'PAN · ANCHORED — MAP + BACKGROUND MOVE TOGETHER'
                    : 'PAN · VIEW — WHOLE EDITOR MOVES TOGETHER')
              : 'SELECT MODE — DRAG ROUTE NODES, PADS, SPAWN, BASE OR PROPS';
    this.render();
    this.applyToolCursor();
  }

  private ensureEnvironment(): MapEnvironment {
    return this.layout.environment ?? (this.layout.environment = { terrain: 'rock', weather: 'clear', intensity: 0.35 });
  }

  private cycleTerrain(): void {
    const environment = this.ensureEnvironment();
    const values: MapEnvironment['terrain'][] = ['rock', 'snow', 'lava', 'acid'];
    environment.terrain = values[(values.indexOf(environment.terrain) + 1) % values.length];
    this.markDirty(`TERRAIN PALETTE ${environment.terrain.toUpperCase()} ASSIGNED`);
    this.render();
  }

  private cycleWeather(): void {
    const environment = this.ensureEnvironment();
    const values: MapEnvironment['weather'][] = ['clear', 'snow', 'acid-rain', 'meteors', 'orbital-lasers', 'alien-assault'];
    environment.weather = values[(values.indexOf(environment.weather) + 1) % values.length];
    if (environment.weather === 'clear') environment.intensity = 0.15;
    this.markDirty(`WEATHER ${environment.weather.toUpperCase()} ASSIGNED`);
    this.render();
  }

  private adjustWeatherIntensity(delta: number): void {
    const environment = this.ensureEnvironment();
    environment.intensity = Phaser.Math.Clamp(Number((environment.intensity + delta).toFixed(2)), 0, 1);
    this.markDirty(`WEATHER INTENSITY ${Math.round(environment.intensity * 100)}%`);
    this.render();
  }

  private drawEditorInspector(): void {
    if (this.devToolsOpen && saveManager.data.settings.devMode) {
      this.drawDevToolsPanel();
      return;
    }
    this.add.text(1020, 80, 'EDITOR INSPECTOR', { fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#e0a744', fontStyle: 'bold', letterSpacing: 1 }).setDepth(101);
    const prop = this.layout.props?.find((candidate) => candidate.id === this.selectedPropId);
    if (prop) {
      this.add.text(1020, 112, MAP_PROP_LABELS[prop.kind], { fontFamily: 'Consolas, monospace', fontSize: '14px', color: '#d9f8ff', fontStyle: 'bold' }).setDepth(101);
      this.add.text(1020, 134, `${prop.height.toUpperCase()} PROP  ·  ${Math.round(prop.x)},${Math.round(prop.y)}`, { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#6f9eaf' }).setDepth(101);
      const textureKey = MAP_PROP_TEXTURES[prop.kind];
      if (this.textures.exists(textureKey)) {
        const frame = MAP_PROP_FRAMES[prop.kind];
        const frameData = frame != null ? this.textures.get(textureKey).get(frame) : undefined;
        const source = frameData ? { width: frameData.cutWidth, height: frameData.cutHeight } : this.textures.get(textureKey).getSourceImage();
        const fit = Math.min(150 / Math.max(1, source.width), 100 / Math.max(1, source.height));
        this.add.image(1135, 208, textureKey, frame).setScale(fit).setDepth(101);
      }
      this.add.text(1020, 274, `ROTATION  ${Math.round(Phaser.Math.RadToDeg(prop.rotation))}°`, { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#9eddf5' }).setDepth(101);
      this.dockButton(1068, 305, 84, 'ROT −15°', () => { this.pushHistory(); prop.rotation -= Phaser.Math.DegToRad(15); this.markDirty('PROP ROTATED'); this.render(); });
      this.dockButton(1160, 305, 84, 'ROT +15°', () => { this.pushHistory(); prop.rotation += Phaser.Math.DegToRad(15); this.markDirty('PROP ROTATED'); this.render(); });
      this.add.text(1020, 346, `UNIFORM SCALE  ${prop.scale.toFixed(2)}`, { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#9eddf5' }).setDepth(101);
      this.dockButton(1068, 377, 84, 'SCALE −', () => this.adjustPropScale(prop, -0.01));
      this.dockButton(1160, 377, 84, 'SCALE +', () => this.adjustPropScale(prop, 0.01));
      this.dockButton(1114, 424, 176, prop.blocksLineOfSight ? 'LOS OCCLUDER: ON' : 'LOS OCCLUDER: OFF', () => { this.pushHistory(); prop.blocksLineOfSight = !prop.blocksLineOfSight; this.losPreview = prop.blocksLineOfSight || this.losPreview; this.markDirty(`LINE OF SIGHT ${prop.blocksLineOfSight ? 'BLOCK ENABLED' : 'BLOCK DISABLED'}`); this.render(); }, prop.blocksLineOfSight, prop.blocksLineOfSight ? 0x3d3020 : 0x152028);
      this.add.text(1020, 460, 'HIGH / MEDIUM / LOW HEIGHT', { fontFamily: 'Consolas, monospace', fontSize: '7px', color: '#6f9eaf' }).setDepth(101);
      this.dockButton(1114, 490, 176, 'DELETE PROP', () => { this.pushHistory(); this.layout.props = (this.layout.props ?? []).filter((candidate) => candidate.id !== prop.id); this.selectedPropId = undefined; this.markDirty('PROP REMOVED'); this.render(); }, false, 0x4a1d24);
    } else if (this.selectedPadIndex >= 0 && this.layout.pads[this.selectedPadIndex]) {
      const pad = this.layout.pads[this.selectedPadIndex];
      const safe = isFieldTurretPlacementSafe(pad, this.layout.routes);
      this.add.text(1020, 112, `MOUNT PAD ${this.selectedPadIndex + 1}`, { fontFamily: 'Consolas, monospace', fontSize: '13px', color: '#d9f8ff', fontStyle: 'bold' }).setDepth(101);
      this.add.text(1020, 140, `${Math.round(pad.x)}, ${Math.round(pad.y)}\n${safe ? 'LEGAL CLEAR TERRAIN' : 'INVALID · TOUCHES ROUTE'}`, { fontFamily: 'Consolas, monospace', fontSize: '9px', color: safe ? '#7dce5a' : '#ff8b8b', lineSpacing: 6 }).setDepth(101);
      this.add.text(1020, 198, 'DRAG THE CYAN FOUNDATION\nOR USE MOUNT MODE TO ADD ANOTHER.', { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#6f9eaf', lineSpacing: 6 }).setDepth(101);
      this.dockButton(1114, 290, 176, 'DELETE PAD', () => { this.pushHistory(); this.layout.pads.splice(this.selectedPadIndex, 1); this.selectedPadIndex = -1; this.markDirty('PAD REMOVED'); this.drawLayout(); }, false, 0x4a1d24);
    } else {
      const route = this.layout.routes[this.selected.route];
      const point = route?.[this.selected.node];
      if (route && point) {
        const endpoint = this.selected.node === 0 ? 'SPAWN POINT' : this.selected.node === route.length - 1 ? 'BASTION END POINT' : 'PATH CONTROL NODE';
        this.add.text(1020, 112, endpoint, { fontFamily: 'Consolas, monospace', fontSize: '13px', color: '#d9f8ff', fontStyle: 'bold' }).setDepth(101);
        this.add.text(1020, 140, `ROUTE ${this.selected.route + 1} · NODE ${this.selected.node + 1}\n${Math.round(point.x)}, ${Math.round(point.y)}`, { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#9eddf5', lineSpacing: 6 }).setDepth(101);
        this.add.text(1020, 198, this.selected.node === 0 || this.selected.node === route.length - 1
          ? 'DRAG THE AMBER HANDLE TO\nREDIRECT THIS ENDPOINT.'
          : 'DRAG THE CYAN HANDLE TO\nREDIRECT THE ROAD. RIGHT-CLICK\nFOR 90° CORNER / DELETE.', { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#6f9eaf', lineSpacing: 6 }).setDepth(101);
        if (this.selected.node > 0 && this.selected.node < route.length - 1) this.dockButton(1114, 290, 176, 'DELETE NODE', () => this.removeSelectedNode(), false, 0x4a1d24);
      }
    }
    this.add.text(1020, 530, `ZOOM  ${Math.round(this.editorZoom * 100)}%`, { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#9eddf5' }).setDepth(101);
    this.dockButton(1080, 558, 58, '−', () => this.zoomEditor(-0.08));
    this.dockButton(1144, 558, 58, '+', () => this.zoomEditor(0.08));
    this.dockButton(1220, 558, 74, this.losPreview ? 'LOS ON' : 'LOS VIEW', () => { this.losPreview = !this.losPreview; this.status = `LINE OF SIGHT PREVIEW ${this.losPreview ? 'ENABLED' : 'HIDDEN'}`; this.render(); }, this.losPreview, this.losPreview ? 0x3d3020 : 0x152028);
  }

  private drawPropPalette(): void {
    const visible = 6;
    const cardW = 116;
    const cardH = 72;
    const gap = 8;
    const maxOffset = Math.max(0, MAP_PROP_KINDS.length - visible);
    this.propPaletteOffset = Phaser.Math.Clamp(this.propPaletteOffset, 0, maxOffset);
    const startX = this.mapViewport.x + 82;
    const y = 660;
    this.dockButton(this.mapViewport.x + 22, y, 32, '‹', () => {
      this.propPaletteOffset = Math.max(0, this.propPaletteOffset - 1);
      this.render();
    }, false, 0x152028, 16);
    this.dockButton(this.mapViewport.x + this.mapViewport.width - 22, y, 32, '›', () => {
      this.propPaletteOffset = Math.min(maxOffset, this.propPaletteOffset + 1);
      this.render();
    }, false, 0x152028, 16);
    this.add.text(this.mapViewport.x + this.mapViewport.width - 52, 599, `${this.propPaletteOffset + 1}–${Math.min(this.propPaletteOffset + visible, MAP_PROP_KINDS.length)} / ${MAP_PROP_KINDS.length}`, { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#6f9eaf' }).setOrigin(1, 0).setDepth(101);
    MAP_PROP_KINDS.slice(this.propPaletteOffset, this.propPaletteOffset + visible).forEach((kind, index) => {
      const x = startX + index * (cardW + gap);
      const active = this.tool === 'prop' && this.activePropKind === kind;
      const bg = this.add.rectangle(x, y, cardW, cardH, active ? 0x163d4d : 0x0f202a, 0.98).setStrokeStyle(1.2, active ? 0x6de9ff : 0x335563).setInteractive({ useHandCursor: true }).setDepth(101);
      addRetroButtonHover(bg, active ? 0x163d4d : 0x0f202a);
      const textureKey = MAP_PROP_TEXTURES[kind];
      if (this.textures.exists(textureKey)) {
        const frame = MAP_PROP_FRAMES[kind];
        const frameData = frame != null ? this.textures.get(textureKey).get(frame) : undefined;
        const source = frameData ? { width: frameData.cutWidth, height: frameData.cutHeight } : this.textures.get(textureKey).getSourceImage();
        const fit = Math.min(68 / Math.max(1, source.width), 40 / Math.max(1, source.height));
        this.add.image(x, y - 6, textureKey, frame).setScale(fit).setDepth(102);
      }
      this.add.text(x, y + 25, MAP_PROP_LABELS[kind], { fontFamily: 'Consolas, monospace', fontSize: '7px', color: active ? '#e5f8ff' : '#9ac5d7', fontStyle: active ? 'bold' : 'normal' }).setOrigin(0.5).setDepth(102);
      bg.on('pointerdown', () => { audio.click(); this.activePropKind = kind; this.selectForgeTool('prop'); });
    });
  }

  private drawDevToolsPanel(): void {
    const settings = saveManager.data.settings;
    this.add.text(1020, 80, 'DEV TOOLS', { fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#e0a744', fontStyle: 'bold', letterSpacing: 1 }).setDepth(101);
    this.add.text(1020, 101, 'GLOBAL TUNING · MAP-SPECIFIC WEIGHTS', { fontFamily: 'Consolas, monospace', fontSize: '7px', color: '#6f9eaf' }).setDepth(101);
    const row = (y: number, label: string, value: string, key: 'devEnemyHpMultiplier' | 'devCreditMultiplier' | 'devStartingSupply' | 'devSquadSize' | 'devBaseIntegrityMultiplier' | 'devBreachDamageMultiplier', min: number, max: number, step: number) => {
      this.add.text(1020, y, label, { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#b7d6e2' }).setDepth(101);
      this.add.text(1180, y, value, { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#f0d49a' }).setOrigin(1, 0).setDepth(101);
      this.dockButton(1208, y + 5, 30, '−', () => this.adjustDevSetting(key, -step, min, max));
      this.dockButton(1250, y + 5, 30, '+', () => this.adjustDevSetting(key, step, min, max));
    };
    row(132, 'HOSTILE HP', `×${settings.devEnemyHpMultiplier.toFixed(2)}`, 'devEnemyHpMultiplier', 0.5, 2.5, 0.1);
    row(172, 'CREDIT EARNING', `×${settings.devCreditMultiplier.toFixed(2)}`, 'devCreditMultiplier', 0.25, 3, 0.1);
    row(212, 'STARTING SUPPLY', `${Math.round(settings.devStartingSupply)}`, 'devStartingSupply', 0, 500, 10);
    row(252, 'SQUAD SIZE', `×${Math.round(settings.devSquadSize)}`, 'devSquadSize', 1, 4, 1);
    row(292, 'BASE INTEGRITY', `×${settings.devBaseIntegrityMultiplier.toFixed(2)}`, 'devBaseIntegrityMultiplier', 0.5, 5, 0.5);
    row(332, 'BREACH DAMAGE', `×${settings.devBreachDamageMultiplier.toFixed(2)}`, 'devBreachDamageMultiplier', 0, 2, 0.1);

    const weights = settings.devMapLaneWeights[this.missionId] ?? [];
    this.add.text(1020, 378, `LANE WEIGHTS · ${this.missionId.toUpperCase()}`, { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#e0a744' }).setDepth(101);
    this.layout.routes.forEach((_route, index) => {
      const x = 1050 + index * 66;
      const weight = Math.max(0, Number(weights[index] ?? 1));
      this.add.text(x, 400, `L${index + 1}  ${weight.toFixed(1)}`, { fontFamily: 'Consolas, monospace', fontSize: '7px', color: '#b7d6e2' }).setOrigin(0.5).setDepth(101);
      this.dockButton(x - 14, 430, 26, '−', () => this.adjustDevLaneWeight(index, -0.1));
      this.dockButton(x + 14, 430, 26, '+', () => this.adjustDevLaneWeight(index, 0.1));
    });
    this.add.text(1020, 474, 'REFRESH MAP SHOT', { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#b7d6e2' }).setDepth(101);
    this.dockButton(1190, 494, 176, 'CAPTURE SAVED LAYOUT', () => { void this.refreshMapShot(); }, false, 0x1c3324);
    this.dockButton(1190, 548, 176, 'CLOSE DEV TOOLS', () => { this.devToolsOpen = false; this.render(); }, false, 0x152028);
  }

  private adjustDevSetting(key: 'devEnemyHpMultiplier' | 'devCreditMultiplier' | 'devStartingSupply' | 'devSquadSize' | 'devBaseIntegrityMultiplier' | 'devBreachDamageMultiplier', delta: number, min: number, max: number): void {
    const settings = saveManager.data.settings;
    settings[key] = Phaser.Math.Clamp(Math.round((Number(settings[key] ?? 0) + delta) * 100) / 100, min, max);
    saveManager.save();
    this.status = `${key.replace(/^dev/, '').replace(/([A-Z])/g, ' $1').trim().toUpperCase()} SET TO ${settings[key]}`;
    this.render();
  }

  private adjustDevLaneWeight(index: number, delta: number): void {
    const settings = saveManager.data.settings;
    settings.devMapLaneWeights[this.missionId] ??= [];
    const weights = settings.devMapLaneWeights[this.missionId];
    weights[index] = Phaser.Math.Clamp(Math.round((Number(weights[index] ?? 1) + delta) * 10) / 10, 0, 5);
    saveManager.save();
    this.status = `LANE ${index + 1} WEIGHT ${weights[index].toFixed(1)} · MAP-SPECIFIC PLAYTEST SETTING`;
    this.render();
  }

  private async refreshMapShot(): Promise<void> {
    if (this.dirty) await this.saveLayout(false);
    this.status = 'MAP SHOT REFRESHED · OPERATIONS BOARD WILL USE THE SAVED LAYOUT';
    this.render();
  }

  private adjustPropScale(prop: MapPropDef, amount: number): void {
    this.pushHistory();
    prop.scale = Phaser.Math.Clamp(Number((prop.scale + amount).toFixed(2)), 0.05, 0.5);
    this.markDirty(`PROP SCALE ${prop.scale.toFixed(2)}`);
    this.render();
  }

  private zoomEditor(amount: number): void {
    this.editorZoom = Phaser.Math.Clamp(Number((this.editorZoom + amount).toFixed(2)), 0.48, 1.6);
    this.status = `EDITOR ZOOM ${Math.round(this.editorZoom * 100)}%`;
    this.render();
    this.applyToolCursor();
  }

  private updateEditorStatus(): void {
    this.toolText?.setText(`ZOOM ${Math.round(this.editorZoom * 100)}% · PAN ${Math.round(this.editorScrollX)},${Math.round(this.editorScrollY)} · PROPS ${(this.layout.props ?? []).length} · ${this.dirty ? 'UNSAVED' : 'SAVED'}`);
  }

  private smallButton(x: number, y: number, width: number, label: string, action: () => void, active = false, colour = 0x152028): void {
    const enabled = label !== 'UNDO' || this.history.length > 0;
    const bg = this.add.rectangle(x, y, width, 28, active ? 0x1d4a5c : colour, enabled ? 1 : 0.5)
      .setStrokeStyle(1, active ? 0x64d7ff : 0x335563)
      .setInteractive({ useHandCursor: enabled });
    if (enabled) addRetroButtonHover(bg, active ? 0x1d4a5c : colour);
    bg.on('pointerdown', () => { if (enabled) { audio.click(); action(); } });
    this.add.text(x, y, label, { fontFamily: 'Consolas, monospace', fontSize: '8px', color: active ? '#e5f8ff' : '#9ac5d7' }).setOrigin(0.5);
  }

  private drawAlignmentControls(_g: Phaser.GameObjects.Graphics): void {
    const plane = this.plane;
    // Docked into the right editor chrome — never a free-floating overlay.
    const x = 1002;
    const y = 420;
    const w = 266;
    const h = 290;
    const root = this.add.container(x, y).setDepth(101);
    this.alignmentPanel = root;
    this.cameras.main?.ignore(root);
    this.uiCam?.ignore?.([]); // keep default: uiCam draws this

    const panel = this.add.graphics();
    panel.fillStyle(0x0b151c, 0.98).fillRoundedRect(0, 0, w, h, 8);
    panel.lineStyle(1.5, 0x5ec8ff, 0.75).strokeRoundedRect(0, 0, w, h, 8);
    panel.fillStyle(0x5ec8ff, 0.15).fillRect(0, 0, w, 3);
    root.add(panel);

    const badge = this.add.graphics();
    badge.lineStyle(1.3, 0x6de9ff, 0.95);
    const bx = 16; const by = 18; const r = 9;
    badge.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = -Math.PI / 2 + (i * Math.PI) / 3;
      const px = bx + Math.cos(a) * r;
      const py = by + Math.sin(a) * r * 0.72;
      if (i === 0) badge.moveTo(px, py); else badge.lineTo(px, py);
    }
    badge.closePath(); badge.strokePath();
    root.add(badge);
    root.add(this.add.text(30, 8, 'ISOMETRIC ALIGNMENT', {
      fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#c4f4ff', fontStyle: 'bold',
    }));
    root.add(this.add.text(30, 22, 'DOCKED EDITOR TOOLS', {
      fontFamily: 'Consolas, monospace', fontSize: '7px', color: '#6f9eaf',
    }));

    const btn = (bx: number, by: number, bw: number, label: string, action: () => void, active = false, colour = 0x152028) => {
      const bg = this.add.rectangle(bx, by, bw, 22, active ? 0x1d4a5c : colour, 1)
        .setStrokeStyle(1, active ? 0x64d7ff : 0x335563)
        .setInteractive({ useHandCursor: true });
      addRetroButtonHover(bg, active ? 0x1d4a5c : colour);
      bg.on('pointerdown', () => { audio.click(); action(); });
      root.add(bg);
      root.add(this.add.text(bx, by, label, {
        fontFamily: 'Consolas, monospace', fontSize: '8px',
        color: active ? '#e5f8ff' : '#9ac5d7', fontStyle: active ? 'bold' : 'normal',
      }).setOrigin(0.5));
    };

    root.add(this.add.text(12, 40, 'PLANE', { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#d4a24a' }));
    btn(48, 56, 72, 'ROT −', () => this.adjustPlane('rotation', -0.08));
    btn(128, 56, 72, 'ROT +', () => this.adjustPlane('rotation', 0.08));
    btn(208, 56, 72, plane.visible ? 'GRID ON' : 'GRID OFF', () => { plane.visible = !plane.visible; this.render(); }, plane.visible, 0x18313b);
    btn(48, 82, 72, 'TILT −', () => this.adjustPlane('tilt', -0.04));
    btn(128, 82, 72, 'TILT +', () => this.adjustPlane('tilt', 0.04));
    btn(208, 82, 72, 'RESET', () => {
      this.layout.plane = { x: GAME_WIDTH / 2, y: (UI_TOP_H + MAP_FIELD_BOTTOM) / 2, rotation: 0, tilt: 0.766, scale: 1, visible: true, farScale: 0.68, nearScale: 1.1 };
      this.markDirty('ALIGNMENT PLANE RESET'); this.render();
    });
    btn(48, 108, 72, 'SCALE −', () => this.adjustPlane('scale', -0.05));
    btn(128, 108, 72, 'SCALE +', () => this.adjustPlane('scale', 0.05));
    btn(208, 108, 72, this.snapStep ? `GRID ${this.snapStep}` : 'GRID FREE', () => {
      this.snapStep = this.snapStep === 0 ? 1 : this.snapStep === 1 ? 2 : this.snapStep === 2 ? 4 : 0;
      this.status = this.snapStep ? `PRECISION GRID ${this.snapStep}px` : 'GRID FREE — PIXEL PRECISION';
      this.saveAlignmentPanelPosition(true);
      this.render();
    }, this.snapStep > 0, 0x172c35);

    root.add(this.add.text(12, 128, 'DEPTH SCALE', { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#d4a24a' }));
    btn(48, 144, 100, `FAR ${plane.farScale?.toFixed(2) ?? '0.68'} −`, () => this.adjustDepthScale('farScale', -0.04));
    btn(164, 144, 100, `FAR ${plane.farScale?.toFixed(2) ?? '0.68'} +`, () => this.adjustDepthScale('farScale', 0.04));
    btn(48, 170, 100, `NEAR ${plane.nearScale?.toFixed(2) ?? '1.10'} −`, () => this.adjustDepthScale('nearScale', -0.05));
    btn(164, 170, 100, `NEAR ${plane.nearScale?.toFixed(2) ?? '1.10'} +`, () => this.adjustDepthScale('nearScale', 0.05));

    root.add(this.add.text(12, 188, 'PAN TARGET  ·  WHAT DRAG MOVES', {
      fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#7dce5a', fontStyle: 'bold',
    }));
    const targets: Array<['view' | 'plate' | 'overlay' | 'anchor', string]> = [
      ['view', 'VIEW'],
      ['plate', 'BG ONLY'],
      ['overlay', 'ISO OVERLAY'],
      ['anchor', 'MAP+BG'],
    ];
    targets.forEach(([key, label], i) => {
      btn(12 + i * 64, 208, 58, label, () => {
        this.panTarget = key;
        this.tool = 'pan';
        this.plateNudgeMode = key === 'plate' || key === 'anchor';
        this.anchorMapToPlate = key === 'anchor';
        this.status = key === 'view' ? 'PAN VIEW — ENTIRE EDITOR MOVES TOGETHER'
          : key === 'plate' ? 'PAN BACKGROUND ONLY — ROUTES/HUD FIXED'
          : key === 'overlay' ? 'PAN ISO OVERLAY ONLY — TERRAIN PLATE FIXED'
          : 'PAN ANCHORED — MAP STAYS LOCKED TO BACKGROUND';
        this.render();
        this.applyToolCursor();
      }, this.panTarget === key, key === 'view' ? 0x152028 : 0x1c3324);
    });

    root.add(this.add.text(12, 228, 'MAP', { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#d4a24a' }));
    btn(48, 244, 100, '+ SPAWN', () => {
      this.tool = 'lane';
      this.status = 'CLICK TERRAIN TO PLACE A SPAWN LANE TO THE BASTION';
      this.render();
    }, this.tool === 'lane', 0x18313b);
    btn(164, 244, 100, this.orthogonal ? 'ORTHO ON' : 'FREEFORM', () => {
      this.orthogonal = !this.orthogonal;
      this.status = this.orthogonal ? 'ORTHOGONAL SNAP ON' : 'FREEFORM CURVES ON';
      this.render();
    }, this.orthogonal, 0x1c3324);
    btn(48, 270, 100, 'IMPORT IMG', () => this.importUserMap(), false, 0x1c3324);
    btn(164, 270, 100, this.libraryOpen ? 'CLOSE MAPS' : 'OPEN MAPS', () => {
      this.libraryOpen = !this.libraryOpen;
      this.render();
    }, this.libraryOpen, 0x18313b);
  }
  private drawUserMapLibrary(_g: Phaser.GameObjects.Graphics): void {
    const maps = listUserMaps();
    const campaigns = MISSIONS.map((mission) => ({
      id: `campaign:${mission.id}`,
      name: mission.name,
      kind: 'campaign' as const,
      missionId: mission.id,
    }));
    const userRows = maps.map((map) => ({
      id: map.id,
      name: map.name,
      kind: 'user' as const,
      visibility: map.visibility ?? 'private',
    }));
    const rows = [...campaigns, ...userRows];
    const w = 420;
    const rowH = 30;
    const header = 72;
    const footer = rows.length ? 48 : 96;
    const h = Math.min(520, header + Math.max(rows.length, 1) * rowH + footer);
    const x = this.mapViewport.x + Math.max(16, (this.mapViewport.width - w) / 2);
    const y = this.mapViewport.y + 24;

    const root = this.add.container(x, y).setDepth(40);
    this.cameras.main?.ignore(root);
    const panel = this.add.graphics();
    panel.fillStyle(0x07131c, 0.96).fillRoundedRect(0, 0, w, h, 10);
    panel.lineStyle(2, 0x6de9ff, 0.85).strokeRoundedRect(0, 0, w, h, 10);
    panel.fillStyle(0x5ec8ff, 0.12).fillRect(0, 0, w, 3);
    root.add(panel);
    root.add(this.add.text(16, 12, 'OPEN MAPS', { fontFamily: 'Consolas, monospace', fontSize: '13px', color: '#e0a744', fontStyle: 'bold', letterSpacing: 1 }));
    root.add(this.add.text(16, 32, 'CAMPAIGN LAYOUTS + YOUR AUTHORED FIELDS', { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#7096a8' }));
    const close = this.add.text(w - 18, 14, '×', { fontFamily: 'Consolas, monospace', fontSize: '16px', color: '#8ab4c4' })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    close.on('pointerover', () => close.setColor('#ff6a6a'));
    close.on('pointerout', () => close.setColor('#8ab4c4'));
    close.on('pointerdown', () => { audio.click(); this.libraryOpen = false; this.render(); });
    root.add(close);

    if (!rows.length) {
      root.add(this.add.text(16, 80, 'NO MAPS YET', { fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#d8f6ff' }));
      root.add(this.add.text(16, 104, 'Create a field from scratch, or import a background image\nto author routes and pads on top of your art.', {
        fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#9fb2c0', lineSpacing: 4,
      }));
      const newBtn = this.add.rectangle(w / 2, 170, 160, 30, 0x1c3b30, 1)
        .setStrokeStyle(1.5, 0x7dce5a)
        .setInteractive({ useHandCursor: true });
      addRetroButtonHover(newBtn, 0x1c3b30);
      newBtn.on('pointerdown', () => { audio.click(); this.libraryOpen = false; this.openNewMapModal(); });
      root.add(newBtn);
      root.add(this.add.text(w / 2, 170, 'NEW MAP', { fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#b8f39d', fontStyle: 'bold' }).setOrigin(0.5));
      return;
    }

    const visible = rows.slice(0, 12);
    visible.forEach((row, index) => {
      const rowY = header + index * rowH + 14;
      const isActive = (row.kind === 'user' && row.id === this.userMapId)
        || (row.kind === 'campaign' && !this.userMap && row.missionId === this.missionId);
      const bg = this.add.rectangle(w / 2, rowY, w - 28, 26, isActive ? 0x1d4a5c : 0x102028, 0.98)
        .setStrokeStyle(1, isActive ? 0x64d7ff : 0x2d596b)
        .setInteractive({ useHandCursor: true });
      bg.on('pointerover', () => bg.setFillStyle(isActive ? 0x245a70 : 0x18313b, 1));
      bg.on('pointerout', () => bg.setFillStyle(isActive ? 0x1d4a5c : 0x102028, 0.98));
      bg.on('pointerdown', () => {
        audio.click();
        if (row.kind === 'campaign') {
          this.userMap = undefined;
          this.userMapId = undefined;
          this.loadMission(row.missionId);
          this.libraryOpen = false;
          this.render();
          return;
        }
        const stored = getUserMap(row.id);
        if (!stored) {
          this.status = 'MAP NOT FOUND IN LOCAL LIBRARY';
          this.render();
          return;
        }
        this.libraryOpen = false;
        this.loadUserMap(stored);
      });
      root.add(bg);
      const tag = row.kind === 'campaign' ? 'CAMPAIGN' : 'USER';
      root.add(this.add.text(20, rowY, tag, {
        fontFamily: 'Consolas, monospace', fontSize: '7px',
        color: row.kind === 'campaign' ? '#d4a24a' : '#7dce5a',
      }).setOrigin(0, 0.5));
      root.add(this.add.text(92, rowY, row.name.slice(0, 28), {
        fontFamily: 'Consolas, monospace', fontSize: '10px', color: isActive ? '#e5f8ff' : '#c8d4e0',
      }).setOrigin(0, 0.5));
      if (row.kind === 'user') {
        const clone = this.add.rectangle(w - 78, rowY, 64, 20, 0x1c3324, 1)
          .setStrokeStyle(1, 0x3d6b52)
          .setInteractive({ useHandCursor: true });
        clone.on('pointerdown', () => {
          audio.click();
          const source = getUserMap(row.id);
          if (source) this.cloneUserMap(source);
        });
        root.add(clone);
        root.add(this.add.text(w - 78, rowY, 'CLONE', { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#9ad4a8' }).setOrigin(0.5));
        const del = this.add.rectangle(w - 28, rowY, 22, 20, 0x3d161c, 1)
          .setStrokeStyle(1, 0xff6a6a)
          .setInteractive({ useHandCursor: true });
        del.on('pointerdown', () => {
          audio.click();
          deleteUserMap(row.id);
          if (this.userMapId === row.id) this.loadMission(this.missionId);
          else this.render();
        });
        root.add(del);
        root.add(this.add.text(w - 28, rowY, '×', { fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#ff8b8b' }).setOrigin(0.5));
      }
    });

    const footY = header + visible.length * rowH + 28;
    const newMap = this.add.rectangle(w / 2 - 90, footY, 160, 28, 0x1c3b30, 1)
      .setStrokeStyle(1.2, 0x7dce5a)
      .setInteractive({ useHandCursor: true });
    newMap.on('pointerdown', () => { audio.click(); this.libraryOpen = false; this.openNewMapModal(); });
    root.add(newMap);
    root.add(this.add.text(w / 2 - 90, footY, 'NEW MAP', { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#b8f39d', fontStyle: 'bold' }).setOrigin(0.5));
    const importBtn = this.add.rectangle(w / 2 + 90, footY, 160, 28, 0x152028, 1)
      .setStrokeStyle(1.2, 0x335563)
      .setInteractive({ useHandCursor: true });
    importBtn.on('pointerdown', () => { audio.click(); this.libraryOpen = false; this.importUserMap(); });
    root.add(importBtn);
    root.add(this.add.text(w / 2 + 90, footY, 'IMPORT IMAGE', { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#9ad4e8' }).setOrigin(0.5));
  }

  private createBlankUserMap(): void {
    this.openNewMapModal();
  }

  private openNewMapModal(): void {
    this.closeSaveAsModal();
    if (typeof document === 'undefined') return;
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;background:rgba(2,7,12,.82);font-family:Consolas,monospace;color:#d8f6ff;';
    const card = document.createElement('div');
    card.style.cssText = 'width:390px;padding:22px;background:#0b1720;border:1px solid #58c9e8;border-radius:8px;box-shadow:0 0 34px rgba(41,184,224,.22);';
    card.innerHTML = '<div style="font-size:15px;letter-spacing:1px;margin-bottom:8px">NEW MAP FROM SCRATCH</div><div style="font-size:10px;color:#7da5b8;margin-bottom:14px;line-height:1.5">Start with an optional background image. The first lane is a clean spawn-to-bastion line; fit the perspective plane, then add routes and pads.</div>';
    const input = document.createElement('input');
    input.placeholder = 'MAP NAME'; input.maxLength = 40;
    input.style.cssText = 'box-sizing:border-box;width:100%;padding:9px 10px;background:#071017;border:1px solid #335563;color:#e5f8ff;font:12px Consolas,monospace;outline:none;';
    const lanes = document.createElement('select');
    lanes.innerHTML = '<option value="1">1 LANE · SINGLE APPROACH</option><option value="2">2 LANES · SPLIT APPROACH</option><option value="3">3 LANES · TRIAD APPROACH</option><option value="4">4 LANES · QUAD APPROACH</option>';
    lanes.style.cssText = 'box-sizing:border-box;width:100%;margin-top:10px;padding:9px 10px;background:#071017;border:1px solid #335563;color:#e5f8ff;font:11px Consolas,monospace;outline:none;';
    const plate = document.createElement('select');
    plate.innerHTML = '<option value="">START WITH EMPTY PLATE</option><option value="/assets/art/maps/island-archipelago-v1.png">ARCHIPELAGO RUN · 2 LANES → 1 EXIT</option><option value="/assets/art/maps/island-merge-v1.png">ISLAND MERGE · 3 LANES → 1 EXIT</option><option value="/assets/art/maps/island-ice-v1.png">ICE CAUSEWAY · 4 LANES → 1 EXIT</option><option value="/assets/art/maps/island-reference-sheet.jpg">ISLAND REFERENCE SHEET</option>';
    plate.style.cssText = 'box-sizing:border-box;width:100%;margin-top:10px;padding:9px 10px;background:#071017;border:1px solid #335563;color:#e5f8ff;font:11px Consolas,monospace;outline:none;';
    const file = document.createElement('input'); file.type = 'file'; file.accept = 'image/*'; file.style.display = 'none';
    const fileButton = document.createElement('button'); fileButton.textContent = 'CHOOSE BACKGROUND IMAGE';
    fileButton.style.cssText = 'margin-top:12px;width:100%;padding:10px;background:#152028;border:1px solid #335563;color:#aeeaff;font:10px Consolas,monospace;cursor:pointer;';
    const fileLabel = document.createElement('div'); fileLabel.textContent = 'NO IMAGE SELECTED · YOU CAN ADD ONE LATER';
    fileLabel.style.cssText = 'font-size:9px;color:#7096a8;margin-top:7px;';
    fileButton.onclick = () => file.click();
    file.onchange = () => { const selected = file.files?.[0]; fileLabel.textContent = selected ? `READY · ${selected.name.slice(0, 42)}` : 'NO IMAGE SELECTED · YOU CAN ADD ONE LATER'; };
    const actions = document.createElement('div'); actions.style.cssText = 'display:flex;justify-content:flex-end;gap:8px;margin-top:18px;';
    const makeButton = (label: string, primary = false) => { const button = document.createElement('button'); button.textContent = label; button.style.cssText = `padding:8px 14px;background:${primary ? '#1d4a5c' : '#152028'};border:1px solid ${primary ? '#64d7ff' : '#335563'};color:#d8f6ff;font:10px Consolas,monospace;cursor:pointer;`; return button; };
    const cancel = makeButton('CANCEL'); const create = makeButton('CREATE MAP', true);
    const commit = () => {
      const name = input.value.trim().replace(/\s+/g, ' ').slice(0, 40) || 'UNTITLED FIELD';
      const selected = file.files?.[0];
      const finish = (background?: string) => { const map = createUserMap(name, background, true, Number(lanes.value)); map.author = saveManager.data.commanderName || 'ARC-CADET'; saveUserMap(map); this.loadUserMap(map); this.status = `${map.name} CREATED — ${lanes.value} LANE${lanes.value === '1' ? '' : 'S'} READY · FIT THE PLANE, THEN ADD PADS`; this.closeSaveAsModal(); this.render(); };
      if (!selected) { finish(plate.value || undefined); return; }
      const reader = new FileReader(); reader.onload = () => finish(String(reader.result)); reader.readAsDataURL(selected);
    };
    cancel.onclick = () => this.closeSaveAsModal(); create.onclick = commit;
    input.onkeydown = (event) => { if (event.key === 'Enter') commit(); if (event.key === 'Escape') this.closeSaveAsModal(); };
    actions.append(cancel, create); card.append(input, lanes, plate, fileButton, fileLabel, file, actions); overlay.append(card); overlay.onclick = (event) => { if (event.target === overlay) this.closeSaveAsModal(); }; document.body.append(overlay);
    this.renameOverlay = overlay; this.renameInput = input; input.focus();
  }

  private cloneUserMap(source: UserMapDef): void {
    const copy = createUserMap(`${source.name} COPY`, source.backgroundDataUrl, true);
    copy.layout = cloneLayout(source.layout); copy.author = saveManager.data.commanderName || 'ARC-CADET'; copy.visibility = 'private';
    saveUserMap(copy); this.loadUserMap(copy); this.status = `${copy.name} CLONED — PRIVATE DRAFT`; this.render();
  }

  private toggleMapVisibility(): void {
    if (!this.userMap) { this.status = 'SAVE AS FIRST — CAMPAIGN LAYOUTS STAY LOCAL'; this.render(); return; }
    this.userMap.visibility = this.userMap.visibility === 'public' ? 'private' : 'public';
    this.userMap.author = saveManager.data.commanderName || 'ARC-CADET';
    saveUserMap(this.userMap);
    if (this.userMap.visibility === 'public') void mapService.publish(this.userMap);
    else void mapService.unpublish(this.userMap.id);
    this.status = this.userMap.visibility === 'public' ? 'MAP MARKED PUBLIC — SHARED MAP LIBRARY UPDATED' : 'MAP PRIVATE — REMOVED FROM SHARED LIBRARY'; this.render();
  }

  private openSaveAsModal(): void {
    this.closeSaveAsModal();
    if (typeof document === 'undefined') return;
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;background:rgba(2,7,12,.78);font-family:Consolas,monospace;color:#d8f6ff;';
    const card = document.createElement('div');
    card.style.cssText = 'width:360px;padding:22px;background:#0b1720;border:1px solid #58c9e8;border-radius:8px;box-shadow:0 0 34px rgba(41,184,224,.22);';
    card.innerHTML = '<div style="font-size:14px;letter-spacing:1px;margin-bottom:8px">SAVE MAP AS</div><div style="font-size:10px;color:#7da5b8;margin-bottom:14px">Create a named copy of this edited layout. Public copies appear in the shared player map library.</div>';
    const input = document.createElement('input');
    input.value = this.userMap?.name ?? `${getMission(this.missionId).name} EDIT`;
    input.maxLength = 40;
    input.style.cssText = 'box-sizing:border-box;width:100%;padding:9px 10px;background:#071017;border:1px solid #335563;color:#e5f8ff;font:12px Consolas,monospace;outline:none;';
    const visibility = document.createElement('select');
    visibility.innerHTML = '<option value="private">PRIVATE · LOCAL MAP</option><option value="public">PUBLIC · SHARE WITH PLAYERS</option>';
    visibility.value = this.userMap?.visibility ?? 'private';
    visibility.style.cssText = 'box-sizing:border-box;width:100%;margin-top:10px;padding:9px 10px;background:#071017;border:1px solid #335563;color:#e5f8ff;font:11px Consolas,monospace;outline:none;';
    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;justify-content:flex-end;gap:8px;margin-top:16px;';
    const makeButton = (label: string, primary = false) => {
      const button = document.createElement('button');
      button.textContent = label;
      button.style.cssText = `padding:8px 14px;background:${primary ? '#1d4a5c' : '#152028'};border:1px solid ${primary ? '#64d7ff' : '#335563'};color:#d8f6ff;font:10px Consolas,monospace;cursor:pointer;`;
      return button;
    };
    const cancel = makeButton('CANCEL');
    const save = makeButton('SAVE MAP', true);
    const commit = async () => {
      const name = input.value.trim().replace(/\s+/g, ' ').slice(0, 40);
      if (!name) { input.focus(); return; }
      const map = createUserMap(name, this.userMap?.backgroundDataUrl);
      map.layout = cloneLayout(this.layout);
      map.visibility = visibility.value === 'public' ? 'public' : 'private';
      map.author = saveManager.data.commanderName || 'ARC-CADET';
      saveUserMap(map);
      const sharedSaved = map.visibility !== 'public' || await mapService.publish(map);
      this.userMap = map;
      this.userMapId = map.id;
      this.dirty = !sharedSaved;
      this.status = map.visibility === 'public'
        ? (sharedSaved ? `${map.name} SAVED · PUBLIC COPY UPDATED FOR PLAYERS` : `${map.name} SAVED LOCALLY · PUBLIC SHARE FAILED, RETRY SAVE`)
        : `${map.name} SAVED · PRIVATE COPY READY TO PLAY TEST`;
      this.closeSaveAsModal();
      this.render();
    };
    cancel.onclick = () => this.closeSaveAsModal();
    save.onclick = () => { void commit(); };
    input.onkeydown = (event) => {
      if (event.key === 'Enter' || event.key === 'Escape') event.stopPropagation();
      if (event.key === 'Enter') void commit();
      if (event.key === 'Escape') this.closeSaveAsModal();
    };
    actions.append(cancel, save);
    card.append(input, visibility, actions);
    overlay.append(card);
    overlay.onclick = (event) => { if (event.target === overlay) this.closeSaveAsModal(); };
    document.body.append(overlay);
    this.renameOverlay = overlay;
    this.renameInput = input;
    input.focus(); input.select();
  }

  private closeSaveAsModal(): void {
    this.renameOverlay?.remove();
    this.renameOverlay = undefined;
    this.renameInput = undefined;
  }

  private importUserMap(): void {
    if (typeof document === 'undefined') return;
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const label = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]+/g, ' ').trim().toUpperCase();
        const map = createUserMap(label || 'IMPORTED FIELD', String(reader.result));
        try {
          saveUserMap(map);
          this.loadUserMap(map);
          this.status = `${map.name} IMPORTED — PLACE LANES AND PADS, THEN SAVE`;
        } catch {
          this.status = 'IMAGE IMPORT FAILED — TRY A SMALLER IMAGE';
          this.render();
        }
      };
      reader.readAsDataURL(file);
    };
    input.click();
  }


  private planeButton(x: number, y: number, width: number, label: string, action: () => void, active = false, colour = 0x152028, parent?: Phaser.GameObjects.Container): void {
    const bg = this.add.rectangle(x, y, width, 22, active ? 0x1d4a5c : colour, 1)
      .setStrokeStyle(1, active ? 0x64d7ff : 0x335563)
      .setInteractive({ useHandCursor: true });
    addRetroButtonHover(bg, active ? 0x1d4a5c : colour);
    bg.on('pointerdown', () => { audio.click(); action(); });
    const text = this.add.text(x, y, label, { fontFamily: 'Consolas, monospace', fontSize: '7px', color: active ? '#e5f8ff' : '#8fc1d4' }).setOrigin(0.5);
    if (parent) parent.add([bg, text]);
  }

  private adjustDepthScale(which: 'farScale' | 'nearScale', amount: number): void {
    const plane = this.plane;
    const current = plane[which] ?? (which === 'farScale' ? 0.68 : 1.1);
    const bounds = which === 'farScale' ? { min: 0.45, max: 0.95 } : { min: 0.9, max: 1.45 };
    plane[which] = Phaser.Math.Clamp(Number((current + amount).toFixed(2)), bounds.min, bounds.max);
    this.markDirty(`${which === 'farScale' ? 'FAR' : 'NEAR'} DEPTH SCALE ${plane[which].toFixed(2)} — PLAY TEST TO REVIEW TOWER PERSPECTIVE`);
    this.render();
  }

  private loadAlignmentPanelPosition(): void {
    if (this.alignmentPanelPositionLoaded || typeof localStorage === 'undefined') return;
    this.alignmentPanelPositionLoaded = true;
    try {
      const raw = localStorage.getItem('apogee-hold-mapforge-panel-position-v2')
        ?? localStorage.getItem('apogee-hold-mapforge-panel-position-v1');
      if (!raw) return;
      const parsed = JSON.parse(raw) as { version?: number; x?: number; y?: number; snapStep?: number };
      if (Number.isFinite(parsed.x)) this.alignmentPanelX = Number(parsed.x);
      if (Number.isFinite(parsed.y)) this.alignmentPanelY = Number(parsed.y);
      // Legacy v1 stored the old 2px default. Preserve the panel position,
      // but only restore an explicit grid choice from the v2 preference.
      if (parsed.version === 2 && (parsed.snapStep === 0 || parsed.snapStep === 1 || parsed.snapStep === 2 || parsed.snapStep === 4)) this.snapStep = parsed.snapStep;
    } catch { /* ignore malformed local editor preference */ }
  }

  private saveAlignmentPanelPosition(preserveStatus = false): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem('apogee-hold-mapforge-panel-position-v2', JSON.stringify({ version: 2, x: this.alignmentPanelX, y: this.alignmentPanelY, snapStep: this.snapStep }));
    if (!preserveStatus) {
      this.status = 'ALIGNMENT PANEL DOCKED — POSITION SAVED FOR THIS BROWSER';
      this.statusText?.setText(this.status);
    }
  }

  private adjustPlane(property: 'rotation' | 'tilt' | 'scale', amount: number): void {
    const plane = this.plane;
    const corners = plane.corners ?? this.defaultPlaneCorners();
    const centre = corners.reduce((sum, point) => ({ x: sum.x + point.x / 4, y: sum.y + point.y / 4 }), { x: 0, y: 0 });
    const transformed = corners.map((point) => {
      let dx = point.x - centre.x; let dy = point.y - centre.y;
      if (property === 'rotation') {
        const cos = Math.cos(amount); const sin = Math.sin(amount);
        [dx, dy] = [dx * cos - dy * sin, dx * sin + dy * cos];
      } else if (property === 'scale') {
        const factor = Phaser.Math.Clamp(1 + amount, 0.55, 1.45);
        dx *= factor; dy *= factor;
      } else {
        dy *= Phaser.Math.Clamp(1 + amount, 0.3 / plane.tilt, 0.82 / plane.tilt);
      }
      return { x: Phaser.Math.Clamp(Math.round(centre.x + dx), 28, BATTLE_W - 28), y: Phaser.Math.Clamp(Math.round(centre.y + dy), UI_TOP_H + 28, GAME_HEIGHT - 98) };
    }) as MapPerspectivePlane['corners'];
    plane.corners = transformed;
    if (property === 'rotation') plane.rotation += amount;
    else if (property === 'tilt') plane.tilt = Phaser.Math.Clamp(plane.tilt + amount, 0.3, 0.82);
    else plane.scale = Phaser.Math.Clamp(plane.scale + amount, 0.55, 1.45);
    this.markDirty(`ALIGNMENT PLANE ${property.toUpperCase()} ADJUSTED`);
    this.render();
  }

  private ensurePlane(): void {
    if (!this.layout.plane) {
      this.layout.plane = { x: GAME_WIDTH / 2, y: (UI_TOP_H + MAP_FIELD_BOTTOM) / 2, rotation: 0, tilt: 0.766, scale: 1, visible: true, farScale: 0.68, nearScale: 1.1, corners: this.defaultPlaneCorners() };
    } else if (!this.layout.plane.corners) {
      this.layout.plane.corners = this.defaultPlaneCorners();
    }
    this.layout.plane.farScale ??= 0.68;
    this.layout.plane.nearScale ??= 1.1;
  }

  private defaultPlaneCorners(): [PathPoint, PathPoint, PathPoint, PathPoint] {
    return [
      { x: 84, y: 238 }, { x: BATTLE_W - 104, y: 212 },
      { x: BATTLE_W - 74, y: MAP_FIELD_BOTTOM - 42 }, { x: 64, y: MAP_FIELD_BOTTOM - 18 },
    ];
  }

  private get plane(): MapPerspectivePlane {
    this.ensurePlane();
    return this.layout.plane!;
  }

  /** Projects a flat local grid through a rotated, vertically compressed plane. */
  private projectPlane(localX: number, localY: number): PathPoint {
    const plane = this.plane;
    if (plane.corners) {
      const u = Phaser.Math.Clamp(localX / 1220 + 0.5, 0, 1);
      const v = Phaser.Math.Clamp(localY / 720 + 0.5, 0, 1);
      const [tl, tr, br, bl] = plane.corners;
      const top = { x: Phaser.Math.Linear(tl.x, tr.x, u), y: Phaser.Math.Linear(tl.y, tr.y, u) };
      const bottom = { x: Phaser.Math.Linear(bl.x, br.x, u), y: Phaser.Math.Linear(bl.y, br.y, u) };
      return { x: Phaser.Math.Linear(top.x, bottom.x, v), y: Phaser.Math.Linear(top.y, bottom.y, v) };
    }
    // Legacy fallback for layouts saved before the four-corner plane existed.
    const px = (localX - localY) * 0.8660254 * plane.scale;
    const py = (localX + localY) * plane.tilt * plane.scale;
    const cos = Math.cos(plane.rotation);
    const sin = Math.sin(plane.rotation);
    return { x: plane.x + px * cos - py * sin, y: plane.y + px * sin + py * cos };
  }

  private drawPerspectivePlane(time: number): void {
    const g = this.planeGraphics;
    if (!g) return;
    const plane = this.plane;
    g.clear();
    this.planeHandleLayer?.setVisible(plane.visible);
    if (!plane.visible) return;
    const gridHalfW = 610;
    const gridHalfH = 360;
    g.fillStyle(0x0a2030, this.snapStep > 0 ? 0.055 : 0);
    const corners = [this.projectPlane(-gridHalfW, -gridHalfH), this.projectPlane(gridHalfW, -gridHalfH), this.projectPlane(gridHalfW, gridHalfH), this.projectPlane(-gridHalfW, gridHalfH)];
    g.fillPoints(corners, true);
    if (this.snapStep > 0) {
      for (let x = -gridHalfW; x <= gridHalfW; x += 32) {
        const a = this.projectPlane(x, -gridHalfH);
        const b = this.projectPlane(x, gridHalfH);
        g.lineStyle(x === 0 ? 1.8 : 1, x === 0 ? 0x7ae7ff : 0x4db6d2, x === 0 ? 0.42 : 0.16);
        g.lineBetween(a.x, a.y, b.x, b.y);
      }
      for (let y = -gridHalfH; y <= gridHalfH; y += 32) {
        const a = this.projectPlane(-gridHalfW, y);
        const b = this.projectPlane(gridHalfW, y);
        g.lineStyle(y === 0 ? 1.8 : 1, y === 0 ? 0x7ae7ff : 0x4db6d2, y === 0 ? 0.42 : 0.16);
        g.lineBetween(a.x, a.y, b.x, b.y);
      }
    }
    g.lineStyle(1.5, 0x6de9ff, 0.34);
    g.strokePoints(corners, true);
    // The old amber centre square implied that the plane itself was a map
    // object. The plane is an authoring guide only, so keep its affordance
    // entirely cyan and let the corner handles communicate its bounds.
    const pulse = 9 + Math.sin(time / 220) * 2;
    g.lineStyle(1, 0xa9f4ff, 0.42);
    g.strokeCircle(plane.x, plane.y, pulse);
    this.syncPlaneHandlePositions();
  }

  private addPerspectivePlaneHandles(): void {
    const layer = this.planeHandleLayer;
    if (!layer) return;
    const plane = this.plane;
    const move = this.add.circle(plane.x, plane.y, 10, 0x0b2c3a, 0.95)
      .setStrokeStyle(2, 0x6de9ff)
      .setInteractive({ draggable: true, useHandCursor: true });
    const rotatePoint = this.projectPlane(0, -220);
    const rotate = this.add.circle(rotatePoint.x, rotatePoint.y, 9, 0x0b2c3a, 0.96)
      .setStrokeStyle(2, 0x6de9ff)
      .setInteractive({ draggable: true, useHandCursor: true });
    this.planeMoveHandle = move;
    this.planeRotateHandle = rotate;
    this.planeCornerHandles = (plane.corners ?? this.defaultPlaneCorners()).map((corner, index) => {
      const handle = this.add.circle(corner.x, corner.y, 9, 0x0b2c3a, 0.98)
        .setStrokeStyle(2, 0x6de9ff)
        .setInteractive({ draggable: true, useHandCursor: true });
      handle.on('dragstart', () => this.pushHistory());
      handle.on('drag', (_pointer: Phaser.Input.Pointer, x: number, y: number) => {
        const corners = this.plane.corners ?? this.defaultPlaneCorners();
        corners[index] = { x: Phaser.Math.Clamp(Math.round(x), 0, BATTLE_W), y: Phaser.Math.Clamp(Math.round(y), UI_TOP_H, MAP_FIELD_BOTTOM) };
        plane.corners = corners;
        handle.setPosition(corners[index].x, corners[index].y);
        this.drawPerspectivePlane(this.time.now);
      });
      handle.on('dragend', () => { this.markDirty(`PLANE CORNER ${index + 1} MOVED — PERSPECTIVE FIT UPDATED`); });
      this.input.setDraggable(handle);
      return handle;
    });
    move.on('dragstart', () => this.pushHistory());
    move.on('drag', (_pointer: Phaser.Input.Pointer, x: number, y: number) => {
      const nextX = Phaser.Math.Clamp(x, 80, GAME_WIDTH - 80);
      const nextY = Phaser.Math.Clamp(y, UI_TOP_H + 60, GAME_HEIGHT - 130);
      const dx = nextX - plane.x; const dy = nextY - plane.y;
      plane.corners = (plane.corners ?? this.defaultPlaneCorners()).map((corner) => ({ x: corner.x + dx, y: corner.y + dy })) as MapPerspectivePlane['corners'];
      plane.x = nextX;
      plane.y = nextY;
      this.drawPerspectivePlane(this.time.now);
    });
    move.on('dragend', () => this.markDirty('ALIGNMENT PLANE MOVED — GRID NOW SITS OVER THE TERRAIN'));
    rotate.on('dragstart', () => { this.pushHistory(); this.planeRotationDragging = true; });
    rotate.on('drag', (_pointer: Phaser.Input.Pointer, x: number, y: number) => {
      const centre = this.planeCentre();
      const nextRotation = Math.atan2(y - centre.y, x - centre.x) + Math.PI / 2;
      const amount = nextRotation - plane.rotation;
      const cos = Math.cos(amount); const sin = Math.sin(amount);
      plane.corners = (plane.corners ?? this.defaultPlaneCorners()).map((corner) => {
        const dx = corner.x - centre.x; const dy = corner.y - centre.y;
        return { x: centre.x + dx * cos - dy * sin, y: centre.y + dx * sin + dy * cos };
      }) as MapPerspectivePlane['corners'];
      plane.x = centre.x; plane.y = centre.y; plane.rotation = nextRotation;
      this.drawPerspectivePlane(this.time.now);
    });
    rotate.on('dragend', () => { this.planeRotationDragging = false; this.markDirty('ALIGNMENT PLANE ROTATED'); });
    this.input.setDraggable(move);
    this.input.setDraggable(rotate);
    layer.add([move, rotate, ...this.planeCornerHandles]);
  }

  private syncPlaneHandlePositions(): void {
    if (!this.planeMoveHandle || !this.planeRotateHandle || this.planeRotationDragging) return;
    const plane = this.plane;
    const centre = this.planeCentre();
    plane.x = centre.x; plane.y = centre.y;
    this.planeMoveHandle.setPosition(plane.x, plane.y);
    const rotate = this.projectPlane(0, -220);
    this.planeRotateHandle.setPosition(rotate.x, rotate.y);
    const corners = this.plane.corners ?? this.defaultPlaneCorners();
    this.planeCornerHandles.forEach((handle, index) => handle.setPosition(corners[index].x, corners[index].y));
  }

  private planeCentre(): PathPoint {
    const corners = this.plane.corners ?? this.defaultPlaneCorners();
    return corners.reduce((sum, point) => ({ x: sum.x + point.x / 4, y: sum.y + point.y / 4 }), { x: 0, y: 0 });
  }

  private drawLayout(): void {
    const handles = this.handleLayer;
    if (!handles) return;
    this.redrawRoads();
    this.releaseDragState();
    handles.removeAll(true);
    this.nodeHandles.clear();
    this.drawProps();
    this.layout.routes.forEach((route, routeIndex) => {
      route.forEach((point, nodeIndex) => this.addNodeHandle(point, routeIndex, nodeIndex));
    });
    this.layout.pads.forEach((pad, index) => this.addPadHandle(pad, index));
    this.addSpawnHandles();
    this.addBaseHandle();
  }

  private drawProps(): void {
    const handles = this.handleLayer;
    if (!handles) return;
    for (const prop of this.layout.props ?? []) {
      const textureKey = MAP_PROP_TEXTURES[prop.kind];
      const frame = MAP_PROP_FRAMES[prop.kind];
      const frameData = this.textures.exists(textureKey) && frame != null ? this.textures.get(textureKey).get(frame) : undefined;
      const source = frameData ? { width: frameData.cutWidth, height: frameData.cutHeight } : this.textures.exists(textureKey) ? this.textures.get(textureKey).getSourceImage() : undefined;
      const image = this.textures.exists(textureKey)
        ? this.add.image(prop.x, prop.y, textureKey, frame)
          .setDisplaySize((source?.width ?? 70) * prop.scale, (source?.height ?? 46) * prop.scale)
        : this.add.rectangle(prop.x, prop.y, 70, 46, 0x263c46, 0.88);
      image.setRotation(prop.rotation).setDepth(2 + prop.depth)
        .setData('forgeContextTarget', { kind: 'prop', id: prop.id } as ContextTarget)
        .setInteractive({ draggable: true, useHandCursor: true });
      const selected = prop.id === this.selectedPropId;
      const ring = this.add.ellipse(prop.x, prop.y, Math.max(42, image.displayWidth * 0.48), Math.max(28, image.displayHeight * 0.48), 0x42d9ff, 0.08)
        .setStrokeStyle(selected ? 2 : 1, selected ? 0xffcb69 : 0x56cde8, selected ? 0.95 : 0.42)
        .setData('forgeContextTarget', { kind: 'prop', id: prop.id } as ContextTarget);
      ring.setVisible(selected || this.losPreview && prop.blocksLineOfSight);
      if (this.losPreview && prop.blocksLineOfSight) {
        ring.setFillStyle(0xffb24e, 0.1).setStrokeStyle(2, 0xffb24e, 0.78);
      }
      let moved = false;
      const select = () => {
        this.selectedPropId = prop.id;
        this.selectedPadIndex = -1;
        this.status = `${MAP_PROP_LABELS[prop.kind]} SELECTED — USE THE INSPECTOR OR DRAG TO POSITION`;
      };
      image.on('pointerdown', () => { if (this.tool !== 'pan') select(); });
      ring.on('pointerdown', () => { if (this.tool !== 'pan') select(); });
      image.on('dragstart', () => { moved = true; this.pushHistory(); });
      image.on('drag', (_pointer: Phaser.Input.Pointer, x: number, y: number) => {
        prop.x = Phaser.Math.Clamp(Math.round(x), 0, BATTLE_W);
        prop.y = Phaser.Math.Clamp(Math.round(y), UI_TOP_H, MAP_FIELD_BOTTOM);
        image.setPosition(prop.x, prop.y); ring.setPosition(prop.x, prop.y);
      });
      image.on('dragend', () => { this.markDirty(`${MAP_PROP_LABELS[prop.kind]} MOVED — ${prop.blocksLineOfSight ? 'LINE OF SIGHT OCCLUDER' : 'DECORATIVE'}`); this.render(); });
      image.on('pointerup', () => { if (!moved && this.tool !== 'pan') this.render(); });
      this.input.setDraggable(image);
      handles.add([image, ring]);
    }
  }

  /** Repaint only the route layer while a handle is being dragged. */
  /** Drop holes / non-finite nodes so a half-corrupt layout never crashes redraw. */
  private normalizeLayoutRoutes(): void {
    const command = this.layout.command && Number.isFinite(this.layout.command.x) && Number.isFinite(this.layout.command.y)
      ? { ...this.layout.command }
      : { x: BATTLE_W - 100, y: 420 };
    this.layout.command = command;
    const fallbackEntry = this.layout.entry && Number.isFinite(this.layout.entry.x) && Number.isFinite(this.layout.entry.y)
      ? { ...this.layout.entry }
      : { x: 72, y: 420 };
    this.layout.entry = fallbackEntry;
    const routes = (this.layout.routes ?? []).map((route) => {
      const points = (Array.isArray(route) ? route : [])
        .filter((point): point is PathPoint => !!point && Number.isFinite(point.x) && Number.isFinite(point.y))
        .map((point) => ({ x: point.x, y: point.y }));
      if (points.length === 0) return [fallbackEntry, { ...command }];
      if (points.length === 1) return [{ ...points[0] }, { ...command }];
      points[points.length - 1] = { ...command };
      return points;
    });
    this.layout.routes = routes.length ? routes : [[fallbackEntry, { ...command }]];
  }

  private redrawRoads(): void {
    const g = this.layoutGraphics;
    if (!g) return;
    this.normalizeLayoutRoutes();
    g.clear();
    const fieldBottom = MAP_FIELD_BOTTOM;
    const halfWidth = (y: number) => getRoadHalfWidth(y, UI_TOP_H, fieldBottom);
    this.layout.routes.forEach((route, routeIndex) => {
      const renderRoute = smoothRoadPoints(route, 4);
      const ribbon = buildRoadRibbon(renderRoute, halfWidth, 0);
      g.fillStyle(0x020609, 0.65).fillPoints([...ribbon.left, ...ribbon.right.slice().reverse()], true);
      g.lineStyle(2, routeIndex === this.selected.route ? 0x66ddff : 0x2684a0, 0.9);
      g.beginPath(); renderRoute.forEach((point, index) => index ? g.lineTo(point.x, point.y) : g.moveTo(point.x, point.y)); g.strokePath();
    });
  }

  private addNodeHandle(point: PathPoint, route: number, node: number): void {
    const selected = this.selected.route === route && this.selected.node === node;
    const isEndpoint = node === 0 || node === this.layout.routes[route].length - 1;
    // Make control points unmissable on dark imported plates. Endpoints stay
    // amber; internal points stay cyan so the road shape is editable at a
    // glance without opening a side panel.
    const handle = this.add.circle(point.x, point.y, selected ? 12 : 9, isEndpoint ? 0xd4a24a : selected ? 0x7fe6ff : 0x15495d, 1)
      .setStrokeStyle(2.5, selected ? 0xffffff : isEndpoint ? 0xffc66d : 0x74d8f6)
      .setData('forgeContextTarget', { kind: 'node', route, node } as ContextTarget)
      .setInteractive({ draggable: true, useHandCursor: true });
    handle.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.selected = { route, node };
      this.selectedPadIndex = -1;
      this.selectedPropId = undefined;
      this.tool = 'select';
      this.status = `ROUTE ${route + 1} · NODE ${node + 1} SELECTED`;
      // Do not rebuild handles here: destroying the object under the pointer
      // cancels Phaser's drag stream and was the cause of sticky nodes.
      this.redrawRoads();
    });
    handle.on('dragstart', () => this.pushHistory());
    handle.on('drag', (_pointer: Phaser.Input.Pointer, dragX: number, dragY: number) => {
      const path = this.layout.routes[route];
      if (!path) return;
      if (node === 0 || node === path.length - 1) {
        this.moveNode(route, node, dragX, dragY);
        const moved = this.layout.routes[route]?.[node];
        if (moved) handle.setPosition(moved.x, moved.y);
        this.redrawRoads();
        return;
      }
      if (!this.activeNodeDrag) {
        this.activeNodeDrag = { route, node, target: { ...path[node] }, velocity: { x: 0, y: 0 }, released: false, settleMs: 0 };
      }
      const point = this.snap({ x: dragX, y: dragY }, this.orthogonal, path, node);
      this.activeNodeDrag.target = point;
      // While the pointer is held, use direct manipulation. Spring physics
      // here makes a free drag look like snapping and can pull neighbours.
      this.activeNodeDrag.velocity = { x: 0, y: 0 };
      path[node] = { ...point };
      handle.setPosition(point.x, point.y);
      this.syncRouteNodeHandles(route);
      this.redrawRoads();
    });
    handle.on('dragend', () => {
      const path = this.layout.routes[route];
      if (node === 0 || node === (path?.length ?? 0) - 1) {
        this.markDirty('ROUTE UPDATED — SAVE LAYOUT OR PLAY TEST'); this.drawLayout();
      } else if (this.activeNodeDrag) {
        this.activeNodeDrag.released = true;
        this.activeNodeDrag.settleMs = 0;
        this.status = 'ROUTE SETTLED — SAVE LAYOUT OR PLAY TEST';
        const settlingDrag = this.activeNodeDrag;
        // The point is already at the pointer target; do not spring it back
        // or pull adjacent points after release.
        this.finishNodeInertia(settlingDrag);
      }
    });
    this.input.setDraggable(handle);
    this.nodeHandles.set(`${route}:${node}`, handle);
    this.handleLayer?.add(handle);
  }

  private addPadHandle(pad: PathPoint, index: number): void {
    const safe = isFieldTurretPlacementSafe(pad, this.layout.routes);
    const handle = this.add.circle(pad.x, pad.y, 13, safe ? 0x0a2531 : 0x3d161c, 0.92)
      .setStrokeStyle(2, safe ? 0x75ddff : 0xff5a5a)
      .setData('forgeContextTarget', { kind: 'pad', index } as ContextTarget)
      .setInteractive({ draggable: true, useHandCursor: true });
    const selected = index === this.selectedPadIndex;
    const label = this.add.text(pad.x, pad.y, selected ? `PAD ${index + 1}` : String(index + 1), { fontFamily: 'Consolas, monospace', fontSize: selected ? '9px' : '8px', color: safe ? '#d9f8ff' : '#ffd0d0' }).setOrigin(0.5);
    if (selected) handle.setRadius(17).setStrokeStyle(2, 0xffcf66);
    handle.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.tool === 'erase-pad') {
        this.pushHistory(); this.layout.pads.splice(index, 1); this.markDirty('PAD REMOVED'); this.drawLayout();
      } else { this.selectedPadIndex = index; this.selectedPropId = undefined; this.status = `PAD ${index + 1} SELECTED — DRAG THE HIGHLIGHTED FOUNDATION ONTO CLEAR TERRAIN`; }
    });
    let previous = { ...this.layout.pads[index] };
    handle.on('dragstart', () => { previous = { ...this.layout.pads[index] }; this.pushHistory(); });
    handle.on('drag', (_pointer: Phaser.Input.Pointer, x: number, y: number) => {
      this.layout.pads[index] = { id: index, ...this.snap({ x, y }, false) };
      const moved = this.layout.pads[index];
      const safeNow = isFieldTurretPlacementSafe(moved, this.layout.routes);
      handle.setPosition(moved.x, moved.y).setFillStyle(safeNow ? 0x0a2531 : 0x3d161c).setStrokeStyle(2, safeNow ? 0x75ddff : 0xff5a5a);
      label.setPosition(moved.x, moved.y).setColor(safeNow ? '#d9f8ff' : '#ffd0d0');
    });
    handle.on('dragend', () => {
      if (!isFieldTurretPlacementSafe(this.layout.pads[index], this.layout.routes)) {
        this.layout.pads[index] = previous; this.status = 'PAD REJECTED — TURRET FOUNDATIONS CANNOT TOUCH A ROUTE'; this.drawLayout();
      } else this.markDirty('PAD MOVED — LEGAL GROUND PLACEMENT');
      this.drawLayout();
    });
    this.input.setDraggable(handle);
    this.handleLayer?.add([handle, label]);
  }

  private addSpawnHandles(): void {
    this.layout.routes.forEach((route, routeIndex) => this.addSpawnHandle(route[0], routeIndex));
  }

  private addSpawnHandle(point: PathPoint, routeIndex: number): void {
    const display = this.spawnDisplayPoint(point, routeIndex);
    const spawn = this.add.star(display.x, display.y, 4, 7, 15, 0xff7040, 1).setStrokeStyle(2, 0xffccb0)
      .setData('forgeContextTarget', { kind: 'spawn', route: routeIndex } as ContextTarget)
      .setInteractive({ draggable: true, useHandCursor: true });
    const label = this.add.text(display.x, display.y - 23, `SPAWN ${routeIndex + 1}`, { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#ffb18f' }).setOrigin(0.5);
    spawn.on('dragstart', () => this.pushHistory());
    spawn.on('drag', (_pointer: Phaser.Input.Pointer, x: number, y: number) => {
      this.setRouteSpawn(routeIndex, this.snap({ x, y }, false));
      const moved = this.layout.routes[routeIndex][0];
      const nextDisplay = this.spawnDisplayPoint(moved, routeIndex);
      spawn.setPosition(nextDisplay.x, nextDisplay.y);
      label.setPosition(nextDisplay.x, nextDisplay.y - 23);
      this.redrawRoads();
    });
    spawn.on('dragend', () => { this.markDirty(`SPAWN ${routeIndex + 1} MOVED — THIS LANE NOW ENTERS SEPARATELY`); this.drawLayout(); });
    this.input.setDraggable(spawn); this.handleLayer?.add([spawn, label]);
  }

  /** Offset stacked starts just enough that each lane's spawn remains grabbable. */
  private spawnDisplayPoint(point: PathPoint, routeIndex: number): PathPoint {
    const sharesEntry = this.layout.routes.slice(0, routeIndex)
      .some((route) => Math.hypot(route[0].x - point.x, route[0].y - point.y) < 18);
    return sharesEntry ? { x: point.x + 18 * routeIndex, y: point.y - 20 * routeIndex } : point;
  }

  private addBaseHandle(): void {
    const point = this.layout.command;
    const base = this.textures.exists('command-bastion-v1')
      ? this.add.image(point.x, point.y, 'command-bastion-v1').setDisplaySize(56, 56)
      : this.add.rectangle(point.x, point.y, 36, 36, 0x123a50);
    base.setData('forgeContextTarget', { kind: 'base' } as ContextTarget);
    base.setInteractive({ draggable: true, useHandCursor: true });
    const label = this.add.text(point.x, point.y - 42, 'BASTION', { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#9fe4ff' }).setOrigin(0.5);
    base.on('pointerdown', () => { this.selected = { route: 0, node: Math.max(0, (this.layout.routes[0]?.length ?? 1) - 1) }; this.selectedPadIndex = -1; this.selectedPropId = undefined; this.status = 'BASTION SELECTED — DRAG THE COMMAND CORE TO REDIRECT EVERY LANE'; this.render(); });
    base.on('dragstart', () => this.pushHistory());
    base.on('drag', (_pointer: Phaser.Input.Pointer, x: number, y: number) => {
      this.setBase(this.snap({ x, y }, false));
      base.setPosition(this.layout.command.x, this.layout.command.y);
      label.setPosition(this.layout.command.x, this.layout.command.y - 42);
      this.redrawRoads();
    });
    base.on('dragend', () => { this.markDirty('BASTION MOVED — ALL ROUTES NOW TERMINATE HERE'); this.drawLayout(); });
    this.input.setDraggable(base); this.handleLayer?.add([base, label]);
  }

  private openContextMenu(screenX: number, screenY: number, target: ContextTarget): void {
    if (this.contextMenu) {
      this.closeContextMenu();
      return;
    }
    const menuW = 300;
    const rowH = 34;
    const options: Array<{ label: string; action: () => void; tone?: number }> = [];
    const worldPoint = this.cameras.main?.getWorldPoint(screenX, screenY) ?? { x: screenX, y: screenY };
    const pointAtTarget = (): PathPoint => {
      if (target.kind === 'node') return this.layout.routes[target.route]?.[target.node] ?? { x: screenX, y: screenY };
      if (target.kind === 'pad') return this.layout.pads[target.index] ?? { x: screenX, y: screenY };
      if (target.kind === 'spawn') return this.layout.routes[target.route]?.[0] ?? { x: screenX, y: screenY };
      if (target.kind === 'base') return this.layout.command;
      if (target.kind === 'prop') return this.layout.props?.find((prop) => prop.id === target.id) ?? worldPoint;
      return worldPoint;
    };
    const point = pointAtTarget();
    const at = this.snap(worldPoint, false);
    const addPad = () => {
      if (!isFieldTurretPlacementSafe(at, this.layout.routes)) { this.status = 'PAD REJECTED — ROUTE EXCLUSION KEEPS TURRETS OFF THE ROAD'; this.render(); return; }
      if (this.layout.pads.length >= 12) { this.status = 'PAD LIMIT REACHED — REMOVE OR MOVE AN EXISTING FOUNDATION'; this.render(); return; }
      this.pushHistory();
      this.layout.pads.push({ id: this.layout.pads.length, ...at });
      this.selectedPadIndex = this.layout.pads.length - 1;
      this.markDirty(`PAD ${this.selectedPadIndex + 1} ADDED — HIGHLIGHTED AMBER RING IS READY TO DRAG`);
      this.drawLayout();
    };
    const addLane = () => {
      this.addSpawnLane(at);
    };
    const insertNode = () => {
      const routeIndex = target.kind === 'node' ? target.route : this.selected.route;
      const route = this.layout.routes[routeIndex];
      if (!route) return;
      const index = target.kind === 'node' ? Math.min(route.length - 1, target.node + 1) : Math.max(1, Math.min(route.length - 1, this.selected.node + 1));
      this.pushHistory();
      route.splice(index, 0, at);
      this.selected = { route: routeIndex, node: index };
      this.markDirty('CONTROL POINT INSERTED — DRAG IT TO FOLLOW A RIDGE, BRIDGE OR TURN');
      this.drawLayout();
    };
    const makeRightAngle = () => {
      const routeIndex = target.kind === 'node' ? target.route : this.selected.route;
      const route = this.layout.routes[routeIndex];
      const nodeIndex = target.kind === 'node' ? target.node : this.selected.node;
      if (!route || nodeIndex <= 0 || nodeIndex >= route.length - 1) {
        this.status = 'SELECT AN INTERNAL CONTROL POINT TO MAKE A 90° CORNER';
        this.render();
        return;
      }
      const before = route[nodeIndex - 1]; const after = route[nodeIndex + 1];
      const candidateA = this.snap({ x: before.x, y: after.y }, false);
      const candidateB = this.snap({ x: after.x, y: before.y }, false);
      const current = route[nodeIndex];
      this.pushHistory();
      route[nodeIndex] = Math.hypot(candidateA.x - current.x, candidateA.y - current.y) < Math.hypot(candidateB.x - current.x, candidateB.y - current.y) ? candidateA : candidateB;
      this.markDirty('90° CORNER SET — PLAY TEST WILL ROUND THE ELBOW');
      this.drawLayout();
    };
    if (target.kind === 'node') {
      options.push({ label: 'INSERT NODE AFTER', action: insertNode });
      if (target.node > 0 && target.node < (this.layout.routes[target.route]?.length ?? 1) - 1) {
        options.push({ label: 'DELETE NODE', action: () => this.deleteNode(target.route, target.node), tone: 0x4a1d24 });
        options.push({ label: 'MAKE 90° CORNER', action: makeRightAngle });
      }
    }
    if (target.kind === 'pad') options.push({ label: 'DELETE PAD', action: () => {
      this.pushHistory(); this.layout.pads.splice(target.index, 1); this.selectedPadIndex = -1; this.markDirty('PAD REMOVED'); this.drawLayout();
    }, tone: 0x4a1d24 });
    if (target.kind === 'prop') {
      const prop = this.layout.props?.find((candidate) => candidate.id === target.id);
      if (prop) {
        this.selectedPropId = prop.id;
        options.push({ label: prop.blocksLineOfSight ? 'DISABLE LINE OF SIGHT BLOCK' : 'ENABLE LINE OF SIGHT BLOCK', action: () => {
          this.pushHistory(); prop.blocksLineOfSight = !prop.blocksLineOfSight;
          this.markDirty(`${MAP_PROP_LABELS[prop.kind]} ${prop.blocksLineOfSight ? 'NOW BLOCKS' : 'NO LONGER BLOCKS'} LINE OF SIGHT`); this.render();
        }});
        options.push({ label: 'ROTATE PROP 15°', action: () => { this.pushHistory(); prop.rotation += Phaser.Math.DegToRad(15); this.markDirty('PROP ROTATED'); this.render(); }});
        options.push({ label: 'DELETE PROP', action: () => { this.pushHistory(); this.layout.props = (this.layout.props ?? []).filter((candidate) => candidate.id !== prop.id); this.selectedPropId = undefined; this.markDirty('PROP REMOVED'); this.render(); }, tone: 0x4a1d24 });
      }
    }
    if (target.kind === 'spawn' && target.route > 0) options.push({ label: 'DELETE LANE', action: () => {
      this.pushHistory(); this.layout.routes.splice(target.route, 1); this.selected.route = Math.max(0, this.selected.route - 1); this.markDirty('LANE REMOVED — PRIMARY LANE RETAINED'); this.drawLayout();
    }, tone: 0x4a1d24 });
    options.push({ label: 'INSERT NODE HERE', action: insertNode });
    options.push({ label: 'ADD PAD HERE', action: addPad });
    options.push({ label: 'NEW LANE FROM HERE', action: addLane });
    options.push({ label: 'SET BASTION HERE', action: () => {
      this.pushHistory(); this.setBase(at); this.markDirty('BASTION MOVED — ALL ROUTES NOW TERMINATE HERE'); this.drawLayout();
    }, tone: 0xd4a24a });
    options.push({ label: this.snapStep ? 'SWITCH OFF GRID SNAP' : 'ENABLE GRID SNAP', action: () => {
      this.snapStep = this.snapStep ? 0 : 2;
      this.status = this.snapStep ? 'GRID SNAP ENABLED — 2PX PRECISION' : 'GRID SNAP OFF — FREE POSITIONING ENABLED';
      this.saveAlignmentPanelPosition(true);
      this.render();
    }});
    options.push({ label: 'CANCEL', action: () => undefined });

    const menuH = 36 + options.length * rowH;
    // Clamp inside the tactical canvas: the props palette sits above this depth.
    const x = Phaser.Math.Clamp(screenX, this.mapViewport.x + 8, this.mapViewport.x + this.mapViewport.width - menuW - 8);
    const y = Phaser.Math.Clamp(screenY, this.mapViewport.y + 8, this.mapViewport.y + this.mapViewport.height - menuH - 8);
    const root = this.add.container(x, y).setDepth(40);
    // Built after the camera split, so pin it to the UI camera to avoid a second copy.
    this.cameras.main.ignore(root);
    const panel = this.add.graphics();
    panel.fillStyle(0x08141c, 0.97).fillRoundedRect(0, 0, menuW, menuH, 8);
    panel.lineStyle(2, 0x5ec8ff, 0.95).strokeRoundedRect(0, 0, menuW, menuH, 8);
    root.add(panel);
    root.add(this.add.text(16, 10, `PLACE / EDIT  ·  ${Math.round(point.x)},${Math.round(point.y)}`, { fontFamily: 'Consolas, monospace', fontSize: '11px', fontStyle: 'bold', color: '#ffe0a6' }));
    options.forEach((option, index) => {
      const rowY = 36 + index * rowH;
      const bg = this.add.rectangle(menuW / 2, rowY + 16, menuW - 18, 30, option.tone ?? 0x132832, 0.96)
        .setStrokeStyle(1.2, option.tone ? 0xff6a6a : 0x2d596b)
        .setInteractive({ useHandCursor: true });
      const txt = this.add.text(22, rowY + 16, option.label, { fontFamily: 'Consolas, monospace', fontSize: '11px', fontStyle: 'bold', color: option.tone === 0xd4a24a ? '#ffe0a6' : '#d8f6ff' }).setOrigin(0, 0.5);
      bg.on('pointerover', () => bg.setFillStyle(option.tone ?? 0x1d4a5c, 1));
      bg.on('pointerout', () => bg.setFillStyle(option.tone ?? 0x132832, 0.92));
      bg.on('pointerdown', () => { audio.click(); this.closeContextMenu(); option.action(); });
      root.add([bg, txt]);
    });
    this.contextMenu = root;
  }

  private closeContextMenu(): void {
    this.contextMenu?.destroy(true);
    this.contextMenu = undefined;
  }

  private deleteNode(routeIndex: number, nodeIndex: number): void {
    const route = this.layout.routes[routeIndex];
    if (!route || nodeIndex <= 0 || nodeIndex >= route.length - 1) {
      this.status = 'ENDPOINTS ARE SPAWN / BASTION — MOVE THEM, DO NOT DELETE THEM';
      this.render();
      return;
    }
    this.pushHistory();
    route.splice(nodeIndex, 1);
    this.selected = { route: routeIndex, node: Math.max(1, nodeIndex - 1) };
    this.markDirty('CONTROL POINT DELETED — ROAD REMAINS JOINED');
    this.drawLayout();
  }

  private onCanvasPress(x: number, y: number): void {
    const point = this.snap({ x, y }, this.orthogonal);
    if (this.tool === 'node') {
      this.pushHistory();
      const route = this.layout.routes[this.selected.route] ?? this.layout.routes[0];
      const at = Math.min(route.length - 1, Math.max(1, this.selected.node + 1));
      route.splice(at, 0, this.snap({ x, y }, this.orthogonal, route, at));
      this.selected = { route: this.selected.route, node: at };
      this.markDirty('CONTROL POINT INSERTED — DRAG IT TO FOLLOW A RIDGE, BRIDGE OR TURN');
      this.drawLayout();
    } else if (this.tool === 'pad') {
      if (!isFieldTurretPlacementSafe(point, this.layout.routes)) {
        this.status = 'PAD REJECTED — ROUTE EXCLUSION KEEPS TURRETS OFF THE ROAD'; this.render(); return;
      }
      if (this.layout.pads.length >= 12) { this.status = 'PAD LIMIT REACHED — REMOVE OR MOVE AN EXISTING FOUNDATION'; this.render(); return; }
      this.pushHistory();
      this.layout.pads.push({ id: this.layout.pads.length, ...point });
      this.selectedPadIndex = this.layout.pads.length - 1;
      this.markDirty(`PAD ${this.selectedPadIndex + 1} ADDED — HIGHLIGHTED AMBER RING IS READY TO DRAG`); this.drawLayout();
    } else if (this.tool === 'lane') {
      this.addSpawnLane(this.snap({ x, y }, false));
    } else if (this.tool === 'prop') {
      this.addProp(this.activePropKind, this.snap({ x, y }, false));
    }
  }

  private addProp(kind: MapPropKind, point: PathPoint): void {
    this.pushHistory();
    const props = this.layout.props ?? (this.layout.props = []);
    const prop = makeMapProp(kind, point.x, point.y, props.length);
    props.push(prop);
    this.selectedPropId = prop.id;
    this.tool = 'select';
    this.markDirty(`${MAP_PROP_LABELS[kind]} PLACED — SELECT IT TO EDIT SCALE, ROTATION OR LINE OF SIGHT`);
    this.render();
  }

  /** Add a lane whose final node is always the current Bastion exit. */
  private addSpawnLane(start: PathPoint): void {
    this.pushHistory();
    const command = { ...this.layout.command };
    const midpoint = this.snap({ x: (start.x + command.x) * 0.5, y: start.y }, false);
    this.layout.routes.push([start, midpoint, command]);
    this.selected = { route: this.layout.routes.length - 1, node: 1 };
    this.tool = 'select';
    this.markDirty(`SPAWN ${this.layout.routes.length} ADDED — PATH CONNECTED TO BASTION EXIT`);
    this.drawLayout();
  }

  private removeSelectedNode(): void {
    const route = this.layout.routes[this.selected.route];
    if (!route || this.selected.node <= 0 || this.selected.node >= route.length - 1) {
      this.status = 'ENDPOINTS ARE SPAWN / BASTION — MOVE THEM BY DRAGGING, NOT REMOVING';
      this.render();
      return;
    }
    this.pushHistory();
    route.splice(this.selected.node, 1);
    this.selected.node = Math.max(1, this.selected.node - 1);
    this.markDirty('CONTROL POINT REMOVED — LANE SIMPLIFIED');
    this.render();
  }

  private moveNode(routeIndex: number, nodeIndex: number, x: number, y: number): void {
    const route = this.layout.routes[routeIndex];
    if (!route) return;
    const point = this.snap({ x, y }, this.orthogonal, route, nodeIndex);
    if (nodeIndex === 0) this.setRouteSpawn(routeIndex, point);
    else if (nodeIndex === route.length - 1) this.setBase(point);
    else route[nodeIndex] = point;
  }

  /**
   * A light spring keeps a route feeling like one connected piece of road.
   * The held node eases toward the cursor; adjacent control points inherit a
   * restrained fraction of that motion, then settle after release.
   */
  private stepNodeInertia(delta: number): void {
    const drag = this.activeNodeDrag;
    if (!drag) return;
    const route = this.layout.routes[drag.route];
    if (!route?.[drag.node]) { this.activeNodeDrag = undefined; return; }
    const node = route[drag.node];
    const step = Phaser.Math.Clamp(delta / 16.667, 0.3, 2);
    if (drag.released) drag.settleMs += delta;
    const before = { ...node };
    drag.velocity.x = (drag.velocity.x + (drag.target.x - node.x) * 0.24 * step) * Math.pow(0.72, step);
    drag.velocity.y = (drag.velocity.y + (drag.target.y - node.y) * 0.24 * step) * Math.pow(0.72, step);
    node.x += drag.velocity.x * step;
    node.y += drag.velocity.y * step;
    const moveX = node.x - before.x;
    const moveY = node.y - before.y;
    for (const siblingIndex of [drag.node - 1, drag.node + 1]) {
      // Keep the individual spawn/base anchors deliberate while allowing
      // internal route control points to flex with the selected node.
      if (siblingIndex <= 0 || siblingIndex >= route.length - 1) continue;
      const sibling = route[siblingIndex];
      if (!sibling || !Number.isFinite(sibling.x) || !Number.isFinite(sibling.y)) continue;
      sibling.x += moveX * 0.16;
      sibling.y += moveY * 0.16;
    }
    this.syncRouteNodeHandles(drag.route);
    this.redrawRoads();
    const distance = Math.hypot(drag.target.x - node.x, drag.target.y - node.y);
    const speed = Math.hypot(drag.velocity.x, drag.velocity.y);
    if (drag.released && (drag.settleMs >= 420 || (distance < 0.8 && speed < 0.35))) this.finishNodeInertia(drag);
  }

  private finishNodeInertia(drag: NodeDrag): void {
    if (this.activeNodeDrag !== drag || !drag.released || !this.scene.isActive()) return;
    const node = this.layout.routes[drag.route]?.[drag.node];
    if (!node) { this.activeNodeDrag = undefined; return; }
    node.x = drag.target.x;
    node.y = drag.target.y;
    this.activeNodeDrag = undefined;
    this.markDirty('ROUTE SETTLED — SAVE LAYOUT OR PLAY TEST');
    this.drawLayout();
  }

  private syncRouteNodeHandles(routeIndex: number): void {
    const route = this.layout.routes[routeIndex];
    route?.forEach((point, index) => {
      if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
      this.nodeHandles.get(`${routeIndex}:${index}`)?.setPosition(point.x, point.y);
    });
  }

  private snap(point: PathPoint, ortho: boolean, route?: PathPoint[], index?: number): PathPoint {
    const fieldBottom = MAP_FIELD_BOTTOM;
    const step = this.snapStep;
    let x = Phaser.Math.Clamp(point.x, 0, GAME_WIDTH);
    let y = Phaser.Math.Clamp(point.y, UI_TOP_H, fieldBottom);
    // FREE mode is deliberately free: no grid rounding, no neighbour-axis
    // magnet and no hidden orthogonal correction. The only clamp left is the
    // playable field boundary, so a node follows the pointer pixel-for-pixel.
    if (step === 0) return { x, y };
    if (step > 0) {
      x = Math.round(x / step) * step;
      y = Math.round(y / step) * step;
    }
    if (ortho && route && index !== undefined && index > 0 && index < route.length - 1) {
      const previous = route[index - 1];
      const next = route[index + 1];
      const tolerance = this.snapStep > 0 ? Math.max(10, this.snapStep * 5) : 0;
      const xTargets = [previous.x, next?.x].filter((value): value is number => Number.isFinite(value));
      const yTargets = [previous.y, next?.y].filter((value): value is number => Number.isFinite(value));
      const nearest = (value: number, targets: number[]) => targets.reduce((best, target) => Math.abs(value - target) < Math.abs(value - best) ? target : best, targets[0]);
      const targetX = nearest(x, xTargets);
      const targetY = nearest(y, yTargets);
      const closeX = Math.abs(x - targetX) <= tolerance;
      const closeY = Math.abs(y - targetY) <= tolerance;
      // Snap to a nearby neighbour axis first; this makes a straight run feel
      // magnetic without pulling a deliberately diagonal point across the map.
      if (closeX) x = targetX;
      if (closeY) y = targetY;
      if (!closeX && !closeY) {
        if (Math.abs(x - previous.x) >= Math.abs(y - previous.y)) y = previous.y;
        else x = previous.x;
      }
    }
    return { x, y };
  }

  private setRouteSpawn(routeIndex: number, point: PathPoint): void {
    const route = this.layout.routes[routeIndex];
    if (!route) return;
    route[0] = { ...point };
    if (routeIndex === 0) this.layout.entry = { ...point };
  }

  private setBase(point: PathPoint): void {
    this.layout.command = { ...point };
    this.layout.routes.forEach((route) => {
      if (!Array.isArray(route) || route.length === 0) return;
      route[route.length - 1] = { ...point };
    });
  }

  private pushHistory(): void {
    this.history.push(cloneLayout(this.layout));
    if (this.history.length > 40) this.history.shift();
    this.future = [];
  }

  private undo(): void {
    const previous = this.history.pop();
    if (!previous) return;
    this.future.push(cloneLayout(this.layout)); this.layout = previous; this.dirty = true;
    this.status = 'UNDO — LAYOUT HAS UNSAVED CHANGES'; this.render();
  }

  private redo(): void {
    const next = this.future.pop();
    if (!next) return;
    this.history.push(cloneLayout(this.layout)); this.layout = next; this.dirty = true;
    this.status = 'REDO — LAYOUT HAS UNSAVED CHANGES'; this.render();
  }

  private markDirty(status: string): void {
    this.dirty = true; this.status = status;
    this.statusText?.setText(status);
    this.toolText?.setText(`LANES ${this.layout.routes.length}  ·  NODES ${this.layout.routes.reduce((n, route) => n + route.length, 0)}  ·  PADS ${this.layout.pads.length}/12  ·  UNSAVED`);
  }

  private async saveLayout(showStatus = true): Promise<boolean> {
    if (this.userMap) {
      this.userMap = {
        ...this.userMap,
        layout: cloneLayout(this.layout),
        backgroundDataUrl: this.userMap.backgroundDataUrl,
        updatedAt: Date.now(),
      };
      saveUserMap(this.userMap);
      const sharedSaved = this.userMap.visibility !== 'public' || await mapService.publish(this.userMap);
      this.layout = cloneLayout(this.userMap.layout);
      this.dirty = !sharedSaved;
      this.status = this.userMap.visibility === 'public'
        ? (sharedSaved ? 'MAP SAVED — PUBLIC COPY UPDATED FOR PLAYERS' : 'MAP SAVED LOCALLY — PUBLIC SHARE FAILED, RETRY SAVE')
        : 'MAP SAVED — THIS USER MAP UPDATED IN PLACE';
      if (showStatus) this.render();
      return sharedSaved;
    } else {
      const mission = getMission(this.missionId);
      saveMapLayout(mission.mapId, this.layout);
      const sharedSaved = await mapService.saveCampaignLayout(mission.mapId, this.layout);
      this.layout = cloneLayout(getMapLayout(mission.mapId));
      this.dirty = !sharedSaved;
      this.status = sharedSaved
        ? 'CAMPAIGN MAP SAVED — ALL PLAYERS WILL USE THIS LAYOUT'
        : 'LOCAL COPY SAVED — SHARED CAMPAIGN SAVE FAILED, RETRY SAVE';
      if (showStatus) this.render();
      return sharedSaved;
    }
  }

  private changeMission(delta: number): void {
    this.userMap = undefined;
    this.userMapId = undefined;
    const index = MISSIONS.findIndex((mission) => mission.id === this.missionId);
    const next = (index + delta + MISSIONS.length) % MISSIONS.length;
    this.loadMission(MISSIONS[next].id);
  }
}
